import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import Select from "react-select";
import "./FinanceEntry.css";

const API_BASE_URL = (
  window.location.hostname === "localhost" ||
  window.location.hostname === "127.0.0.1"
    ? "http://localhost:8080"
    : process.env.REACT_APP_API_BASE_URL || "https://erp-api.sdcllp.co.in"
).replace(/\/$/, "");

const COMPANY_BANKS = {
  SDC: ["SDC-HDFC", "SDC-DBS", "SDC-SBI"],
};

const PAYMENT_MODES = ["NEFT", "Cash", "UPI"];

const defaultMasters = {
  officeUnits: [],
  assets: [],
  liabilities: [],
  customers: [],
  incomeHeads: [],
  subincomeHeads: [],
  incomeGroups: [],
  vendors: [],
  expenseHeads: [],
  subexpenseHeads: [],
  expenseGroups: [],
};

const todayDate = () => new Date().toISOString().slice(0, 10);

const getCurrentFinancialYearStart = () => {
  const now = new Date();
  const year = now.getFullYear();
  return now.getMonth() + 1 >= 4 ? year : year - 1;
};

const getFinancialYearLabel = (startYear) =>
  `${startYear}-${String(startYear + 1).slice(-2)}`;

const getFinancialYearForDate = (date) => {
  const d = new Date(date);
  if (Number.isNaN(d.getTime())) return null;
  const year = d.getFullYear();
  return d.getMonth() + 1 >= 4 ? year : year - 1;
};

const getCalendarYearForFinancialMonth = (financialYearStart, month) =>
  Number(month) >= 4
    ? Number(financialYearStart)
    : Number(financialYearStart) + 1;

const getEmptyForm = () => ({
  date: todayDate(),
  amount: "",
  company: "SDC",
  bankAccount: "SDC-HDFC",
  paymentMode: "NEFT",
  gstNo: "",
  gstPercent: "",
  tdsPercent: "",
  tdsSettlementOpen: false,
  tdsSettlementAmount: "",
  tdsProofFile: null,
  tdsProofExistingFileName: "",

  quoteSource: "erp",
  quoteDocumentId: "",
  quoteNo: "",
  quoteFile: null,
  quoteExistingFileName: "",

  proformaSource: "erp",
  proformaDocumentId: "",
  proformaNo: "",
  proformaFile: null,
  proformaExistingFileName: "",

  invoiceSource: "erp",
  invoiceDocumentId: "",
  invoiceNo: "",
  invoiceFile: null,
  invoiceExistingFileName: "",

  billNo: "",
  otherDocFile: null,
  otherDocExistingFileName: "",
  note: "",
});

const getTdsStatus = (entry) => {
  const tdsPercent = Number(entry?.tdsPercent || 0);
  const tdsAmount = Number(entry?.tdsSettlementAmount || 0);

  if (tdsPercent <= 0 && tdsAmount <= 0) return null;

  if (tdsAmount > 0) {
    return {
      label: entry.type === "income" ? "TDS Received" : "TDS Paid",
      className: "tds-status-done",
      amount: tdsAmount,
    };
  }

  return {
    label: "TDS Pending",
    className: "tds-status-pending",
    amount: 0,
  };
};

export default function FinanceEntry({ user }) {
  const [entries, setEntries] = useState([]);
  const [projects, setProjects] = useState([]);
  const [masters, setMasters] = useState(defaultMasters);
  const [popupOpen, setPopupOpen] = useState(false);
  const [entryType, setEntryType] = useState("income");
  const [form, setForm] = useState(getEmptyForm);
  const [loading, setLoading] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState("SDC");
  const [salesDocuments, setSalesDocuments] = useState([]);
  const [purchaseDocuments, setPurchaseDocuments] = useState({
    quotation: [],
    proforma: [],
    invoice: [],
  });
  const [documentsLoading, setDocumentsLoading] = useState(false);
  const [editingEntryId, setEditingEntryId] = useState(null);

  // Invoice payment can also be recorded directly from the Finance card.
  // It uses the SAME invoice payment API as InvoicePage, so both pages stay in sync.
  const [invoicePaymentOpen, setInvoicePaymentOpen] = useState(false);
  const [invoicePaymentDoc, setInvoicePaymentDoc] = useState(null);
  const [invoicePaymentSaving, setInvoicePaymentSaving] = useState(false);
  const [invoicePaymentForm, setInvoicePaymentForm] = useState({
    paymentMethod: "NEFT",
    amount: "",
    paymentDate: todayDate(),
    senderName: "",
    senderBankName: "",
    senderAccountNumber: "",
    transactionNumber: "",
    chequeNumber: "",
    chequeDate: "",
    chequeBankName: "",
    remarks: "",
    proofFile: null,
  });

  // Card status filters are independent for In Flow and Out Flow.
  const [incomeLockFilter, setIncomeLockFilter] = useState("all");
  const [expenseLockFilter, setExpenseLockFilter] = useState("all");

  // Separate Income and Expense filter controls.
  const [incomeFilterType, setIncomeFilterType] = useState("all");
  const [incomeFilterValue, setIncomeFilterValue] = useState("all");
  const [expenseFilterType, setExpenseFilterType] = useState("all");
  const [expenseFilterValue, setExpenseFilterValue] = useState("all");

  const [dateFilter, setDateFilter] = useState({
    mode: "month",
    month: String(new Date().getMonth() + 1),
    quarter: String(
      new Date().getMonth() + 1 >= 4
        ? Math.floor((new Date().getMonth() + 1 - 4) / 3) + 1
        : 4,
    ),
    financialYear: String(getCurrentFinancialYearStart()),
    fromDate: "",
    toDate: "",
  });

  const role = String(
    user?.role || localStorage.getItem("role") || "",
  ).toLowerCase();
  const canLock = ["admin", "accountant head", "accountant"].includes(role);

  const getAuthConfig = () => ({
    headers: {
      Authorization: `Bearer ${localStorage.getItem("token")}`,
      Accept: "application/json",
    },
  });

  useEffect(() => {
    console.log("Finance API Base URL:", API_BASE_URL);

    const initialiseFinance = async () => {
      await syncInvoiceEntries();

      await Promise.all([
        fetchEntries(),
        fetchProjects(),
        fetchSalesDocuments(),
        fetchPurchaseDocuments(),
        fetchMasters(),
      ]);
    };

    initialiseFinance();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const buildAmountValues = (source) => {
    const baseAmount = Number(source?.amount || 0);
    const gstPercent = Number(source?.gstPercent || 0);
    const tdsPercent = Number(source?.tdsPercent || 0);

    const gstAmount = (baseAmount * gstPercent) / 100;
    const tdsAmount = (baseAmount * tdsPercent) / 100;
    const totalAmount = baseAmount + gstAmount - tdsAmount;

    return {
      amount: baseAmount,
      gstPercent,
      gstAmount,
      tdsPercent,
      tdsAmount,
      totalAmount,
    };
  };
  const normalizeEntry = (entry) => {
    const company = entry.company || "SDC";
    const bankAccount =
      entry.bankAccount || COMPANY_BANKS[company]?.[0] || "SDC-HDFC";
    const paymentMode =
      entry.paymentMode && PAYMENT_MODES.includes(entry.paymentMode)
        ? entry.paymentMode
        : "NEFT";

    const amount = Number(entry.amount || 0);
    const gstPercent = Number(entry.gstPercent || 0);
    const tdsPercent = Number(entry.tdsPercent || 0);
    const gstAmount =
      entry.gstAmount !== undefined
        ? Number(entry.gstAmount || 0)
        : (amount * gstPercent) / 100;
    const tdsAmount =
      entry.tdsAmount !== undefined
        ? Number(entry.tdsAmount || 0)
        : (amount * tdsPercent) / 100;
    const totalAmount =
      entry.totalAmount !== undefined
        ? Number(entry.totalAmount || 0)
        : amount + gstAmount - tdsAmount;

    return {
      ...entry,
      company,
      bankAccount,
      paymentMode,
      amount,
      gstPercent,
      gstAmount,
      tdsPercent,
      tdsAmount,
      totalAmount,
      officeUnit: entry.officeUnit || "",
      subHead: entry.subHead || "",
      classificationType:
        entry.classificationType || (entry.classification ? "asset" : ""),
      classification: entry.classification || "",
    };
  };

  const syncInvoiceEntries = async () => {
    try {
      await axios.post(
        `${API_BASE_URL}/api/finance-entries/sync-invoices`,
        {},
        getAuthConfig(),
      );
    } catch (err) {
      console.warn("Invoice finance sync skipped/failed:", {
        status: err.response?.status,
        data: err.response?.data,
        message: err.message,
      });
    }
  };

  const fetchEntries = async () => {
    try {
      const res = await axios.get(
        `${API_BASE_URL}/api/finance-entries`,
        getAuthConfig(),
      );
      const list = Array.isArray(res.data)
        ? res.data
        : Array.isArray(res.data?.data)
          ? res.data.data
          : Array.isArray(res.data?.entries)
            ? res.data.entries
            : [];

      setEntries(list.map(normalizeEntry));
    } catch (err) {
      console.error("Finance entries fetch error:", {
        url: `${API_BASE_URL}/api/finance-entries`,
        status: err.response?.status,
        data: err.response?.data,
        message: err.message,
      });
      setEntries([]);
    }
  };

  const fetchMasters = async () => {
    try {
      const res = await axios.get(
        `${API_BASE_URL}/api/finance-masters`,
        getAuthConfig(),
      );

      const data = res.data?.data || {};

      setMasters({
        officeUnits: Array.isArray(data.officeUnits) ? data.officeUnits : [],
        assets: Array.isArray(data.assets) ? data.assets : [],
        liabilities: Array.isArray(data.liabilities) ? data.liabilities : [],
        customers: Array.isArray(data.customers) ? data.customers : [],
        incomeHeads: Array.isArray(data.incomeHeads) ? data.incomeHeads : [],
        subincomeHeads: Array.isArray(data.subincomeHeads)
          ? data.subincomeHeads
          : [],
        incomeGroups: Array.isArray(data.incomeGroups) ? data.incomeGroups : [],
        vendors: Array.isArray(data.vendors) ? data.vendors : [],
        expenseHeads: Array.isArray(data.expenseHeads) ? data.expenseHeads : [],
        subexpenseHeads: Array.isArray(data.subexpenseHeads)
          ? data.subexpenseHeads
          : [],
        expenseGroups: Array.isArray(data.expenseGroups)
          ? data.expenseGroups
          : [],
      });
    } catch (err) {
      console.error("Finance masters fetch error:", {
        url: `${API_BASE_URL}/api/finance-masters`,
        status: err.response?.status,
        data: err.response?.data,
        message: err.message,
      });
      setMasters({ ...defaultMasters });
    }
  };

  const fetchProjects = async () => {
    try {
      const res = await axios.get(
        `${API_BASE_URL}/api/projects`,
        getAuthConfig(),
      );
      const list = Array.isArray(res.data)
        ? res.data
        : Array.isArray(res.data?.projects)
          ? res.data.projects
          : Array.isArray(res.data?.data)
            ? res.data.data
            : Array.isArray(res.data?.data?.projects)
              ? res.data.data.projects
              : Array.isArray(res.data?.result)
                ? res.data.result
                : Array.isArray(res.data?.results)
                  ? res.data.results
                  : Array.isArray(res.data?.projectData)
                    ? res.data.projectData
                    : [];

      const normalizedProjects = list
        .map((project) => ({
          ...project,
          _id:
            project?._id ||
            project?.id ||
            project?.projectId ||
            project?.project_id ||
            "",
          name:
            project?.name ||
            project?.projectName ||
            project?.project_name ||
            project?.title ||
            "",
        }))
        .filter((project) => project.name);

      setProjects(normalizedProjects);
      console.log("Finance projects loaded:", normalizedProjects.length);

      if (!normalizedProjects.length) {
        console.warn("No projects found in API response:", res.data);
      }
    } catch (err) {
      console.error("Projects fetch error:", {
        url: `${API_BASE_URL}/api/projects`,
        status: err.response?.status,
        data: err.response?.data,
        message: err.message,
      });
      setProjects([]);
    }
  };

  const fetchSalesDocuments = async () => {
    try {
      setDocumentsLoading(true);
      const res = await axios.get(
        `${API_BASE_URL}/api/sales/documents`,
        getAuthConfig(),
      );

      const list = Array.isArray(res.data)
        ? res.data
        : Array.isArray(res.data?.data)
          ? res.data.data
          : [];

      setSalesDocuments(list);
    } catch (err) {
      console.error("Sales documents fetch error:", {
        url: `${API_BASE_URL}/api/sales/documents`,
        status: err.response?.status,
        data: err.response?.data,
        message: err.message,
      });
      setSalesDocuments([]);
    } finally {
      setDocumentsLoading(false);
    }
  };

  const fetchPurchaseDocuments = async () => {
    try {
      setDocumentsLoading(true);

      const [quotationRes, proformaRes, invoiceRes] = await Promise.all([
        axios.get(`${API_BASE_URL}/api/purchase/quotations`, getAuthConfig()),
        axios.get(
          `${API_BASE_URL}/api/purchase/proforma-invoices`,
          getAuthConfig(),
        ),
        axios.get(`${API_BASE_URL}/api/purchase/invoices`, getAuthConfig()),
      ]);

      const getList = (res, key) => {
        const data = res?.data;
        if (Array.isArray(data)) return data;
        if (Array.isArray(data?.[key])) return data[key];
        if (Array.isArray(data?.data)) return data.data;
        if (Array.isArray(data?.data?.[key])) return data.data[key];
        return [];
      };

      const quotations = getList(quotationRes, "quotations").map((doc) => ({
        ...doc,
        _id: doc?._id || doc?.id || "",
        type: "quotation",
        number: doc?.quotationNo || doc?.number || "",
      }));

      const proformas = getList(proformaRes, "proformaInvoices").map((doc) => ({
        ...doc,
        _id: doc?._id || doc?.id || "",
        type: "proforma",
        number:
          doc?.proformaInvoiceNo ||
          doc?.proformaNo ||
          doc?.invoiceNo ||
          doc?.number ||
          "",
      }));

      const invoices = getList(invoiceRes, "invoices").map((doc) => ({
        ...doc,
        _id: doc?._id || doc?.id || "",
        type: "invoice",
        number: doc?.invoiceNo || doc?.number || "",
      }));

      setPurchaseDocuments({
        quotation: quotations,
        proforma: proformas,
        invoice: invoices,
      });
    } catch (err) {
      console.error("Purchase documents fetch error:", err);
      setPurchaseDocuments({
        quotation: [],
        proforma: [],
        invoice: [],
      });
    } finally {
      setDocumentsLoading(false);
    }
  };

  const getDocumentsByType = (type) => {
    // In Flow = Sales documents, Out Flow = Purchase documents.
    if (entryType === "expense") {
      return Array.isArray(purchaseDocuments?.[type])
        ? purchaseDocuments[type]
        : [];
    }

    return salesDocuments.filter(
      (doc) =>
        String(doc?.type || "").toLowerCase() === type &&
        (!doc?.company || doc.company === form.company),
    );
  };

  const getExistingAttachedFiles = (type) => {
    const fieldMap = {
      quotation: "quoteFileName",
      proforma: "proformaFileName",
      invoice: "invoiceFileName",
    };

    const field = fieldMap[type];
    if (!field) return [];

    const names = entries
      .filter((entry) => !entry?.company || entry.company === form.company)
      .map((entry) => String(entry?.[field] || "").trim())
      .filter(Boolean);

    return Array.from(new Set(names)).sort((a, b) =>
      a.localeCompare(b, undefined, { sensitivity: "base" }),
    );
  };

  const handleDocumentSelect = (type, selectedValue) => {
    const isAttachedValue = String(selectedValue || "").startsWith("file:");
    const existingFileName = isAttachedValue
      ? String(selectedValue).slice(5)
      : "";

    const availableDocuments = getDocumentsByType(type);

    const doc = isAttachedValue
      ? null
      : availableDocuments.find((x) => String(x._id) === String(selectedValue));

    setForm((prev) => {
      const next = { ...prev };

      if (type === "quotation") {
        next.quoteFile = null;
        next.quoteExistingFileName = existingFileName;

        if (isAttachedValue) {
          next.quoteSource = "attach-existing";
          next.quoteDocumentId = "";
          next.quoteNo = "";
        } else {
          next.quoteSource = "erp";
          next.quoteDocumentId = selectedValue;
          next.quoteNo = doc?.number || "";
          next.quoteExistingFileName = "";
        }
      }

      if (type === "proforma") {
        next.proformaFile = null;
        next.proformaExistingFileName = existingFileName;

        if (isAttachedValue) {
          next.proformaSource = "attach-existing";
          next.proformaDocumentId = "";
          next.proformaNo = "";
        } else {
          next.proformaSource = "erp";
          next.proformaDocumentId = selectedValue;
          next.proformaNo = doc?.number || "";
          next.proformaExistingFileName = "";
        }
      }

      if (type === "invoice") {
        next.invoiceFile = null;
        next.invoiceExistingFileName = existingFileName;

        if (isAttachedValue) {
          next.invoiceSource = "attach-existing";
          next.invoiceDocumentId = "";
          next.invoiceNo = "";
        } else {
          next.invoiceSource = entryType === "expense" ? "purchase" : "erp";
          next.invoiceDocumentId = selectedValue;
          next.invoiceNo = doc?.number || "";
          next.gstNo = String(doc?.clientGstin || "")
            .trim()
            .toUpperCase();
          next.invoiceExistingFileName = "";
        }
      }

      return next;
    });
  };

  const handleFileChange = (name, file) => {
    setForm((prev) => {
      const next = {
        ...prev,
        [name]: file || null,
      };

      if (name === "otherDocFile" && file) {
        next.otherDocExistingFileName = "";
      }

      if (name === "quoteFile" && file) {
        next.quoteSource = "attach";
        next.quoteDocumentId = "";
        next.quoteNo = "";
        next.quoteExistingFileName = "";
      }

      if (name === "proformaFile" && file) {
        next.proformaSource = "attach";
        next.proformaDocumentId = "";
        next.proformaNo = "";
        next.proformaExistingFileName = "";
      }

      if (name === "invoiceFile" && file) {
        next.invoiceSource = "attach";
        next.invoiceDocumentId = "";
        next.invoiceNo = "";
        next.invoiceExistingFileName = "";
      }

      return next;
    });
  };

  const addMaster = async (key) => {
    const value = prompt("Enter name");
    if (!value?.trim()) return;

    try {
      await axios.post(
        `${API_BASE_URL}/api/finance-masters/${key}`,
        { value: value.trim() },
        getAuthConfig(),
      );

      await fetchMasters();
    } catch (err) {
      console.error("Finance master add error:", err);
      alert(
        err.response?.data?.message ||
          err.response?.data?.error ||
          "Finance master add failed",
      );
    }
  };

  const openPopup = (type) => {
    setEditingEntryId(null);
    setEntryType(type);
    setForm({
      ...getEmptyForm(),
      company: selectedCompany,
      bankAccount: COMPANY_BANKS[selectedCompany]?.[0] || "",
    });
    setPopupOpen(true);
  };

  const openEditPopup = (entry) => {
    const current = normalizeEntry(entry);

    setEditingEntryId(current._id);
    setEntryType(current.type || "income");
    setForm({
      ...getEmptyForm(),
      date: current.date ? String(current.date).slice(0, 10) : todayDate(),
      amount: String(current.amount ?? ""),
      company: current.company || selectedCompany,
      bankAccount:
        current.paymentMode === "Cash" ? "" : current.bankAccount || "",
      paymentMode: current.paymentMode || "NEFT",
      gstNo: String(
        salesDocuments.find(
          (doc) =>
            String(doc._id) ===
            String(
              current.invoiceDocumentId?._id || current.invoiceDocumentId || "",
            ),
        )?.clientGstin ||
          current.gstNo ||
          "",
      )
        .trim()
        .toUpperCase(),
      gstPercent: current.gstPercent ? String(current.gstPercent) : "",
      tdsPercent: current.tdsPercent ? String(current.tdsPercent) : "",
      tdsSettlementOpen: Boolean(
        Number(current.tdsSettlementAmount) || current.tdsProofFileName,
      ),
      tdsSettlementAmount: String(current.tdsSettlementAmount || ""),
      tdsProofFile: null,
      tdsProofExistingFileName: current.tdsProofFileName || "",

      quoteSource: current.quoteSource || "erp",
      quoteDocumentId:
        current.quoteDocumentId?._id || current.quoteDocumentId || "",
      quoteNo: current.quoteNo || "",
      quoteFile: null,
      quoteExistingFileName: current.quoteFileName || "",

      proformaSource: current.proformaSource || "erp",
      proformaDocumentId:
        current.proformaDocumentId?._id || current.proformaDocumentId || "",
      proformaNo: current.proformaNo || "",
      proformaFile: null,
      proformaExistingFileName: current.proformaFileName || "",

      invoiceSource: current.invoiceSource || "erp",
      invoiceDocumentId:
        current.invoiceDocumentId?._id || current.invoiceDocumentId || "",
      invoiceNo: current.invoiceNo || "",
      invoiceFile: null,
      invoiceExistingFileName: current.invoiceFileName || "",

      billNo: current.billNo || "",
      otherDocFile: null,
      otherDocExistingFileName: current.otherDocFileName || "",
      note: current.note || "",
    });
    setPopupOpen(true);
  };

  const closePopup = () => {
    setPopupOpen(false);
    setEditingEntryId(null);
    setForm(getEmptyForm());
  };

  const openInvoicePaymentPopup = (entry) => {
    const invoiceId = String(
      entry?.invoiceDocumentId?._id || entry?.invoiceDocumentId || "",
    );
    const invoiceNo = String(entry?.invoiceNo || "")
      .trim()
      .toLowerCase();

    const document = salesDocuments.find((doc) => {
      const docId = String(doc?._id || "");
      const docNo = String(doc?.number || doc?.invoiceNumber || "")
        .trim()
        .toLowerCase();

      return (
        (invoiceId && docId === invoiceId) || (invoiceNo && docNo === invoiceNo)
      );
    });

    if (!document?._id) {
      alert(
        "Invoice document not found. Please refresh Finance Entry and try again.",
      );
      return;
    }

    const invoiceAmount = Number(
      document.grandTotal || entry?.invoiceAmount || 0,
    );
    const alreadyPaid = Number(document.paidAmount || entry?.paidAmount || 0);
    const balance = Math.max(0, invoiceAmount - alreadyPaid);

    if (balance <= 0) {
      alert("This invoice is already fully paid.");
      return;
    }

    setInvoicePaymentDoc(document);
    setInvoicePaymentForm({
      paymentMethod: "NEFT",
      amount: String(balance),
      paymentDate: todayDate(),
      senderName:
        entry?.partyName ||
        document?.client?.companyName ||
        document?.client?.name ||
        document?.clientName ||
        "",
      senderBankName: "",
      senderAccountNumber: "",
      transactionNumber: "",
      chequeNumber: "",
      chequeDate: "",
      chequeBankName: "",
      remarks: "",
      proofFile: null,
    });
    setInvoicePaymentOpen(true);
  };

  const closeInvoicePaymentPopup = () => {
    if (invoicePaymentSaving) return;
    setInvoicePaymentOpen(false);
    setInvoicePaymentDoc(null);
  };

  const saveInvoicePaymentFromFinance = async () => {
    if (!invoicePaymentDoc?._id) return;

    const invoiceAmount = Number(invoicePaymentDoc.grandTotal || 0);
    const alreadyPaid = Number(invoicePaymentDoc.paidAmount || 0);
    const balance = Math.max(0, invoiceAmount - alreadyPaid);
    const amount = Number(invoicePaymentForm.amount || 0);

    if (amount <= 0) {
      alert("Enter received amount");
      return;
    }

    if (amount > balance + 0.001) {
      alert(
        `Received amount cannot be more than balance ₹ ${balance.toLocaleString("en-IN")}`,
      );
      return;
    }

    if (!invoicePaymentForm.paymentDate) {
      alert("Select payment date");
      return;
    }

    if (!String(invoicePaymentForm.senderName || "").trim()) {
      alert("Enter sender / payer name");
      return;
    }

    if (!String(invoicePaymentForm.senderBankName || "").trim()) {
      alert("Enter sender bank name");
      return;
    }

    if (!invoicePaymentForm.proofFile) {
      alert("Payment proof screenshot/photo is required");
      return;
    }

    if (
      invoicePaymentForm.paymentMethod === "NEFT" &&
      !String(invoicePaymentForm.transactionNumber || "").trim()
    ) {
      alert("Enter NEFT / UTR / Transaction No.");
      return;
    }

    if (invoicePaymentForm.paymentMethod === "CHEQUE") {
      if (!String(invoicePaymentForm.chequeNumber || "").trim()) {
        alert("Enter cheque number");
        return;
      }
      if (!invoicePaymentForm.chequeDate) {
        alert("Select cheque date");
        return;
      }
      if (!String(invoicePaymentForm.chequeBankName || "").trim()) {
        alert("Enter cheque bank name");
        return;
      }
    }

    try {
      setInvoicePaymentSaving(true);

      const fd = new FormData();
      fd.append("paymentMethod", invoicePaymentForm.paymentMethod);
      fd.append("amount", String(amount));
      fd.append("paymentDate", invoicePaymentForm.paymentDate);
      fd.append(
        "senderName",
        String(invoicePaymentForm.senderName || "").trim(),
      );
      fd.append(
        "senderBankName",
        String(invoicePaymentForm.senderBankName || "").trim(),
      );
      fd.append(
        "senderAccountNumber",
        String(invoicePaymentForm.senderAccountNumber || "").trim(),
      );
      fd.append(
        "transactionNumber",
        String(invoicePaymentForm.transactionNumber || "").trim(),
      );
      fd.append(
        "chequeNumber",
        String(invoicePaymentForm.chequeNumber || "").trim(),
      );
      fd.append("chequeDate", invoicePaymentForm.chequeDate || "");
      fd.append(
        "chequeBankName",
        String(invoicePaymentForm.chequeBankName || "").trim(),
      );
      fd.append("remarks", String(invoicePaymentForm.remarks || "").trim());
      fd.append("paymentProof", invoicePaymentForm.proofFile);

      await axios.post(
        `${API_BASE_URL}/api/sales/documents/${invoicePaymentDoc._id}/payments`,
        fd,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
            Accept: "application/json",
          },
        },
      );

      setInvoicePaymentOpen(false);
      setInvoicePaymentDoc(null);
      await Promise.all([fetchSalesDocuments(), fetchEntries()]);
      alert("Payment recorded successfully");
    } catch (err) {
      console.error("Finance invoice payment error:", err);
      alert(
        err.response?.data?.message ||
          err.response?.data?.error ||
          "Failed to record payment",
      );
    } finally {
      setInvoicePaymentSaving(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    setForm((prev) => {
      if (name === "company") {
        return {
          ...prev,
          company: value,
          bankAccount:
            prev.paymentMode === "Cash" ? "" : COMPANY_BANKS[value]?.[0] || "",
          quoteDocumentId: "",
          quoteNo: "",
          quoteExistingFileName: "",
          proformaDocumentId: "",
          proformaNo: "",
          proformaExistingFileName: "",
          invoiceDocumentId: "",
          invoiceNo: "",
          invoiceExistingFileName: "",
        };
      }

      if (name === "paymentMode") {
        const isCash = value === "Cash";

        return {
          ...prev,
          paymentMode: value,
          bankAccount: isCash
            ? ""
            : prev.bankAccount || COMPANY_BANKS[prev.company]?.[0] || "",
          gstPercent: isCash ? "" : prev.gstPercent,
          tdsPercent: isCash ? "" : prev.tdsPercent,
        };
      }

      if (name === "quoteSource") {
        return {
          ...prev,
          quoteSource: value,
          quoteDocumentId: "",
          quoteNo: "",
          quoteFile: null,
        };
      }

      if (name === "proformaSource") {
        return {
          ...prev,
          proformaSource: value,
          proformaDocumentId: "",
          proformaNo: "",
          proformaFile: null,
        };
      }

      if (name === "invoiceSource") {
        return {
          ...prev,
          invoiceSource: value,
          invoiceDocumentId: "",
          invoiceNo: "",
          invoiceFile: null,
        };
      }

      return {
        ...prev,
        [name]: value,
      };
    });
  };

  const updateEntryWithDraggedData = async (entry, data) => {
    if (!entry?._id || !data?.value) return;

    if (entry.locked) {
      alert("Locked entry cannot be updated.");
      return;
    }

    const old = normalizeEntry(entry);

    const nextHead =
      data.type === "incomeHead" || data.type === "expenseHead"
        ? data.value
        : old.head || "";

    const nextSubHead =
      data.type === "subincomeHead" || data.type === "subexpenseHead"
        ? data.value
        : old.subHead || "";

    const nextGroup =
      data.type === "incomeGroup" || data.type === "expenseGroup"
        ? data.value
        : old.groupName || "";

    const nextProjectName =
      data.type === "project" ? data.value : old.projectName || "";

    const nextPartyName =
      data.type === "vendor" || data.type === "customer"
        ? data.value
        : old.partyName || "";

    const nextOfficeUnit =
      data.type === "officeUnit" ? data.value : old.officeUnit || "";

    const nextClassificationType =
      data.type === "asset"
        ? "asset"
        : data.type === "liability"
          ? "liability"
          : old.classificationType || "";

    const nextClassification =
      data.type === "asset" || data.type === "liability"
        ? data.value
        : old.classification || "";

    const payload = {
      type: old.type,
      date: old.date,
      amount: old.amount,
      gstPercent: old.gstPercent,
      gstAmount: old.gstAmount,
      tdsPercent: old.tdsPercent,
      tdsAmount: old.tdsAmount,
      tdsSettlementAmount: old.tdsSettlementAmount || 0,
      tdsProofFileName: old.tdsProofFileName || "",
      tdsProofFileKey: old.tdsProofFileKey || "",
      totalAmount: old.totalAmount,
      company: old.company,
      bankAccount: old.bankAccount,
      paymentMode: old.paymentMode,
      gstNo: old.gstNo || "",

      projectId:
        data.type === "project"
          ? data.id
          : old.projectId?._id || old.projectId || null,

      projectName: nextProjectName,
      partyName: nextPartyName,
      officeUnit: nextOfficeUnit,
      head: nextHead,
      subHead: nextSubHead,
      groupName: nextGroup,
      classificationType: nextClassificationType,
      classification: nextClassification,

      isUnmapped:
        !nextHead ||
        !nextSubHead ||
        !nextGroup ||
        !nextProjectName ||
        !nextOfficeUnit,
      locked: false,
      status: "pending",
    };

    try {
      await axios.put(
        `${API_BASE_URL}/api/finance-entries/${old._id}`,
        payload,
        getAuthConfig(),
      );
      fetchEntries();
    } catch (err) {
      alert(err.response?.data?.message || "Entry update failed");
      console.error("Update entry error:", err);
    }
  };

  const saveEntry = async () => {
    if (!form.date || !form.amount) {
      alert("Date and Amount required");
      return;
    }

    if (
      form.tdsSettlementOpen &&
      (!(Number(form.tdsSettlementAmount) > 0) ||
        (!form.tdsProofFile && !form.tdsProofExistingFileName))
    ) {
      alert("TDS amount aur proof dono required hain");
      return;
    }
    const amountValues = buildAmountValues(form);
    const existingEntry = editingEntryId
      ? entries.find((x) => x._id === editingEntryId)
      : null;

    const payload = {
      type: entryType,
      date: form.date,
      ...amountValues,
      tdsSettlementAmount: form.tdsSettlementOpen
        ? Number(form.tdsSettlementAmount || 0)
        : 0,
      tdsProofFileName:
        form.tdsProofFile?.name || form.tdsProofExistingFileName || "",
      tdsProofFileKey: existingEntry?.tdsProofFileKey || "",
      company: form.company,
      bankAccount: form.paymentMode === "Cash" ? "" : form.bankAccount,
      paymentMode: form.paymentMode,
      gstNo: String(
        (form.invoiceSource === "erp" && form.invoiceDocumentId
          ? salesDocuments.find(
              (doc) => String(doc._id) === String(form.invoiceDocumentId),
            )?.clientGstin
          : null) ??
          form.gstNo ??
          "",
      )
        .trim()
        .toUpperCase(),

      quoteNo: form.quoteNo,
      proformaNo: form.proformaNo,
      invoiceNo: form.invoiceNo,
      billNo: form.billNo,
      note: form.note,

      quoteSource: form.quoteSource,
      quoteDocumentId: form.quoteDocumentId || "",
      quoteFileName: form.quoteFile?.name || form.quoteExistingFileName || "",
      quoteFileKey: existingEntry?.quoteFileKey || "",

      proformaSource: form.proformaSource,
      proformaDocumentId: form.proformaDocumentId || "",
      proformaFileName:
        form.proformaFile?.name || form.proformaExistingFileName || "",
      proformaFileKey: existingEntry?.proformaFileKey || "",

      invoiceSource: form.invoiceSource,
      invoiceDocumentId: form.invoiceDocumentId || "",
      invoiceFileName:
        form.invoiceFile?.name || form.invoiceExistingFileName || "",
      invoiceFileKey: existingEntry?.invoiceFileKey || "",

      otherDocFileName:
        form.otherDocFile?.name || form.otherDocExistingFileName || "",
      otherDocFileKey: existingEntry?.otherDocFileKey || "",

      projectId:
        existingEntry?.projectId?._id || existingEntry?.projectId || "",
      projectName: existingEntry?.projectName || "",
      partyName: existingEntry?.partyName || "",
      officeUnit: existingEntry?.officeUnit || "",
      head: existingEntry?.head || "",
      subHead: existingEntry?.subHead || "",
      groupName: existingEntry?.groupName || "",
      classificationType: existingEntry?.classificationType || "",
      classification: existingEntry?.classification || "",

      isUnmapped: existingEntry ? Boolean(existingEntry.isUnmapped) : true,
    };

    const formData = new FormData();

    Object.entries(payload).forEach(([key, value]) => {
      if (value === undefined || value === null) {
        formData.append(key, "");
      } else {
        formData.append(key, String(value));
      }
    });

    if (form.quoteFile) {
      formData.append("quoteFile", form.quoteFile);
    }

    if (form.proformaFile) {
      formData.append("proformaFile", form.proformaFile);
    }

    if (form.invoiceFile) {
      formData.append("invoiceFile", form.invoiceFile);
    }

    if (form.otherDocFile) {
      formData.append("otherDocFile", form.otherDocFile);
    }
    if (form.tdsSettlementOpen && form.tdsProofFile) {
      formData.append("tdsProofFile", form.tdsProofFile);
    }

    try {
      setLoading(true);

      const config = {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
          Accept: "application/json",
        },
      };

      if (editingEntryId) {
        await axios.put(
          `${API_BASE_URL}/api/finance-entries/${editingEntryId}`,
          formData,
          config,
        );
        alert("Entry updated successfully");
      } else {
        await axios.post(
          `${API_BASE_URL}/api/finance-entries`,
          formData,
          config,
        );
        alert("Entry saved for review");
      }

      closePopup();
      fetchEntries();
    } catch (err) {
      console.error("SAVE ERROR FULL:", err.response?.data);

      alert(
        err.response?.data?.message ||
          err.response?.data?.error ||
          "Entry save failed",
      );
    } finally {
      setLoading(false);
    }
  };

  const openSalesDocumentPdf = async (documentId) => {
    const id =
      typeof documentId === "object"
        ? documentId?._id || documentId?.id || ""
        : documentId || "";

    if (!id) {
      alert("Invoice document ID not found.");
      return;
    }

    try {
      // Open the actual PDF saved for this Sales Invoice.
      // Do not redirect to /sales/invoices and do not create a second preview.
      const response = await axios.get(
        `${API_BASE_URL}/api/sales/documents/${id}/pdf`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
          responseType: "blob",
        },
      );

      const contentType =
        response.headers?.["content-type"] || "application/pdf";

      const pdfBlob = new Blob([response.data], {
        type: contentType,
      });

      const pdfUrl = URL.createObjectURL(pdfBlob);
      const newWindow = window.open(pdfUrl, "_blank", "noopener,noreferrer");

      if (!newWindow) {
        URL.revokeObjectURL(pdfUrl);
        alert("Please allow pop-ups to open the invoice PDF.");
        return;
      }

      setTimeout(() => URL.revokeObjectURL(pdfUrl), 60000);
    } catch (err) {
      console.error("Open Invoice PDF Error:", err);

      let message = "Unable to open the invoice PDF.";

      if (err.response?.data instanceof Blob) {
        try {
          const errorText = await err.response.data.text();
          const parsed = JSON.parse(errorText);
          message = parsed?.message || parsed?.error || message;
        } catch (_) {}
      } else {
        message =
          err.response?.data?.message || err.response?.data?.error || message;
      }

      if (err.response?.status === 404) {
        message =
          "This invoice PDF has not been saved on the server yet. Open the invoice in Sales > Invoices, go to Preview, and click Save Invoice once. The PDF will then be saved automatically and will open directly from Finance.";
      }

      alert(message);
    }
  };
  const openPurchaseDocument = async (entry, doc) => {
    const documentId =
      doc?.documentId?._id ||
      doc?.documentId ||
      (doc?.label === "Invoice"
        ? entry?.invoiceDocumentId?._id || entry?.invoiceDocumentId
        : doc?.label === "Quotation"
          ? entry?.quoteDocumentId?._id || entry?.quoteDocumentId
          : entry?.proformaDocumentId?._id || entry?.proformaDocumentId) ||
      "";

    if (!documentId) {
      alert(`${doc?.label || "Purchase"} document ID not found.`);
      return;
    }

    const config =
      doc?.label === "Quotation"
        ? {
            getUrl: `/api/purchase/quotations/${documentId}`,
            fileUrl: "/api/purchase/quotations/file",
            responseKeys: ["quotation", "data"],
            pageHash: "#/purchase/quotations",
          }
        : doc?.label === "PI"
          ? {
              getUrl: `/api/purchase/proforma-invoices/${documentId}`,
              fileUrl: "/api/purchase/proforma-invoices/file",
              responseKeys: ["proformaInvoice", "invoice", "data"],
              pageHash: "#/purchase/proforma-invoices",
            }
          : {
              getUrl: `/api/purchase/invoices/${documentId}`,
              fileUrl: "/api/purchase/invoices/file",
              responseKeys: ["invoice", "data"],
              pageHash: "#/purchase/invoices",
            };

    try {
      const response = await axios.get(
        `${API_BASE_URL}${config.getUrl}`,
        getAuthConfig(),
      );

      let purchaseDoc = response?.data;
      for (const key of config.responseKeys) {
        if (response?.data?.[key]) {
          purchaseDoc = response.data[key];
          break;
        }
      }

      const files = Array.isArray(purchaseDoc?.attachments)
        ? purchaseDoc.attachments
        : [];

      // If a Purchase document has an uploaded attachment, open that actual file.
      if (files.length > 0) {
        const file = files[0];
        const fileName = file?.fileName || "";

        if (!fileName) {
          alert("Purchase document file name not found.");
          return;
        }

        const fileResponse = await axios.get(
          `${API_BASE_URL}${config.fileUrl}/${encodeURIComponent(fileName)}`,
          {
            ...getAuthConfig(),
            responseType: "blob",
          },
        );

        const contentType =
          fileResponse.headers?.["content-type"] ||
          file?.mimeType ||
          "application/octet-stream";

        const blob = new Blob([fileResponse.data], { type: contentType });
        const blobUrl = window.URL.createObjectURL(blob);
        const newTab = window.open(blobUrl, "_blank");

        if (!newTab) {
          window.URL.revokeObjectURL(blobUrl);
          alert("Popup blocked. Please allow popups to open the document.");
          return;
        }

        setTimeout(() => window.URL.revokeObjectURL(blobUrl), 60000);
        return;
      }

      // No attachment: open the Purchase module record instead.
      if (doc?.label === "Invoice") {
        localStorage.setItem("openPurchaseInvoiceId", String(documentId));
      } else if (doc?.label === "Quotation") {
        localStorage.setItem("openPurchaseQuotationId", String(documentId));
      } else {
        localStorage.setItem("openPurchaseProformaId", String(documentId));
      }

      window.location.hash = config.pageHash;
    } catch (err) {
      console.error("Open Purchase Document Error:", err);
      alert(
        err.response?.data?.message ||
          err.response?.data?.error ||
          `Unable to open Purchase ${doc?.label || "document"}.`,
      );
    }
  };

  const openPurchaseInvoice = async (entry) => {
    return openPurchaseDocument(entry, {
      label: "Invoice",
      documentId:
        entry?.invoiceDocumentId?._id || entry?.invoiceDocumentId || "",
    });
  };

  const lockEntry = async (id) => {
    if (!window.confirm("Lock this entry?")) return;

    try {
      await axios.patch(
        `${API_BASE_URL}/api/finance-entries/${id}/lock`,
        {},
        getAuthConfig(),
      );
      fetchEntries();
    } catch (err) {
      alert(err.response?.data?.message || "Entry lock failed");
      console.error("Lock entry error:", err);
    }
  };

  const availableFinancialYears = useMemo(() => {
    const years = new Set();

    entries.forEach((entry) => {
      if (!entry?.date) return;
      const fy = getFinancialYearForDate(entry.date);
      if (fy !== null) years.add(fy);
    });

    years.add(getCurrentFinancialYearStart());

    return Array.from(years).sort((a, b) => b - a);
  }, [entries]);

  const filteredEntries = useMemo(() => {
    return entries.filter((entry) => {
      if (!entry?.date) return false;
      if ((entry.company || "SDC") !== selectedCompany) return false;

      const d = new Date(entry.date);
      if (Number.isNaN(d.getTime())) return false;

      const financialYearStart = Number(
        dateFilter.financialYear || getCurrentFinancialYearStart(),
      );
      const month = Number(dateFilter.month || 4);
      const quarter = Number(dateFilter.quarter || 1);

      if (dateFilter.mode === "month") {
        const calendarYear = getCalendarYearForFinancialMonth(
          financialYearStart,
          month,
        );

        return d.getFullYear() === calendarYear && d.getMonth() + 1 === month;
      }

      if (dateFilter.mode === "quarter") {
        const quarterRanges = {
          1: {
            from: new Date(financialYearStart, 3, 1),
            to: new Date(financialYearStart, 5, 30, 23, 59, 59, 999),
          },
          2: {
            from: new Date(financialYearStart, 6, 1),
            to: new Date(financialYearStart, 8, 30, 23, 59, 59, 999),
          },
          3: {
            from: new Date(financialYearStart, 9, 1),
            to: new Date(financialYearStart, 11, 31, 23, 59, 59, 999),
          },
          4: {
            from: new Date(financialYearStart + 1, 0, 1),
            to: new Date(financialYearStart + 1, 2, 31, 23, 59, 59, 999),
          },
        };

        const range = quarterRanges[quarter] || quarterRanges[1];
        return d >= range.from && d <= range.to;
      }

      if (dateFilter.mode === "year") {
        const from = new Date(financialYearStart, 3, 1);
        const to = new Date(financialYearStart + 1, 2, 31, 23, 59, 59, 999);

        return d >= from && d <= to;
      }

      if (dateFilter.mode === "custom") {
        const from = dateFilter.fromDate
          ? new Date(`${dateFilter.fromDate}T00:00:00`)
          : null;
        const to = dateFilter.toDate
          ? new Date(`${dateFilter.toDate}T23:59:59.999`)
          : null;

        if (from && d < from) return false;
        if (to && d > to) return false;

        return true;
      }

      return true;
    });
  }, [entries, dateFilter, selectedCompany]);

  const clearDateFilter = () => {
    const now = new Date();
    const currentMonth = now.getMonth() + 1;

    setDateFilter({
      mode: "month",
      month: String(currentMonth),
      quarter: String(
        currentMonth >= 4 ? Math.floor((currentMonth - 4) / 3) + 1 : 4,
      ),
      financialYear: String(getCurrentFinancialYearStart()),
      fromDate: "",
      toDate: "",
    });
  };

  const sortUnlockedFirst = (list) =>
    [...list].sort((a, b) => {
      // Keep unlocked entries at the top and locked entries at the bottom.
      if (Boolean(a.locked) !== Boolean(b.locked)) {
        return a.locked ? 1 : -1;
      }

      // Within the same lock status, show the latest entry first.
      const dateA = new Date(a.date).getTime();
      const dateB = new Date(b.date).getTime();

      if (!Number.isNaN(dateA) && !Number.isNaN(dateB) && dateA !== dateB) {
        return dateB - dateA;
      }

      // Fallback to createdAt when dates are the same or unavailable.
      const createdAtA = new Date(a.createdAt || 0).getTime();
      const createdAtB = new Date(b.createdAt || 0).getTime();
      return createdAtB - createdAtA;
    });

  // All entries for the selected company/date period.
  // These are still used by Cash Flow / P&L / Max summaries so the
  // new card status filter does not change the top financial summary.
  const matchesFinanceFilter = (entry, filterType, filterValue, side) => {
    if (filterType === "all" || filterValue === "all") return true;

    const selectedValue = String(filterValue || "");

    switch (filterType) {
      case "project":
        return String(entry.projectName || "") === selectedValue;

      case "officeUnit":
        return String(entry.officeUnit || "") === selectedValue;

      case "incomeHead":
        return side === "income" && String(entry.head || "") === selectedValue;

      case "subincomeHead":
        return (
          side === "income" && String(entry.subHead || "") === selectedValue
        );

      case "incomeGroup":
        return (
          side === "income" && String(entry.groupName || "") === selectedValue
        );

      case "customer":
        return (
          side === "income" && String(entry.partyName || "") === selectedValue
        );

      case "vendor":
        return (
          side === "expense" && String(entry.partyName || "") === selectedValue
        );

      case "expenseHead":
        return side === "expense" && String(entry.head || "") === selectedValue;

      case "subexpenseHead":
        return (
          side === "expense" && String(entry.subHead || "") === selectedValue
        );

      case "expenseGroup":
        return (
          side === "expense" && String(entry.groupName || "") === selectedValue
        );

      case "asset":
        return (
          String(entry.classificationType || "") === "asset" &&
          String(entry.classification || "") === selectedValue
        );

      case "liability":
        return (
          String(entry.classificationType || "") === "liability" &&
          String(entry.classification || "") === selectedValue
        );

      default:
        return true;
    }
  };

  const allIncomeEntries = sortUnlockedFirst(
    filteredEntries.filter(
      (e) =>
        e.type === "income" &&
        matchesFinanceFilter(e, incomeFilterType, incomeFilterValue, "income"),
    ),
  );

  const allExpenseEntries = sortUnlockedFirst(
    filteredEntries.filter(
      (e) =>
        e.type === "expense" &&
        matchesFinanceFilter(
          e,
          expenseFilterType,
          expenseFilterValue,
          "expense",
        ),
    ),
  );

  const filterByLockStatus = (list, filter) => {
    if (filter === "locked") {
      return list.filter((entry) => Boolean(entry.locked));
    }

    if (filter === "unlocked") {
      return list.filter((entry) => !Boolean(entry.locked));
    }

    return list;
  };

  // ------------------------------------------------------------
  // INVOICE -> FINANCE CARD LINK
  // Every invoice is visible in In Flow even before any payment is received.
  // If a normal Finance Entry already references that invoice, we enrich that
  // existing card instead of showing a duplicate invoice card.
  // ------------------------------------------------------------
  const invoiceFinanceEntries = useMemo(() => {
    const invoiceDocs = salesDocuments.filter(
      (doc) =>
        String(doc?.type || "").toLowerCase() === "invoice" &&
        (!doc?.company || doc.company === selectedCompany),
    );

    const actualInvoiceIds = new Set(
      entries
        .map((entry) =>
          String(
            entry?.invoiceDocumentId?._id || entry?.invoiceDocumentId || "",
          ),
        )
        .filter(Boolean),
    );

    const actualInvoiceNumbers = new Set(
      entries
        .map((entry) =>
          String(entry?.invoiceNo || "")
            .trim()
            .toLowerCase(),
        )
        .filter(Boolean),
    );

    return invoiceDocs
      .filter((doc) => {
        const documentId = String(doc?._id || "");
        const invoiceNo = String(doc?.number || doc?.invoiceNumber || "")
          .trim()
          .toLowerCase();

        return (
          (!documentId || !actualInvoiceIds.has(documentId)) &&
          (!invoiceNo || !actualInvoiceNumbers.has(invoiceNo))
        );
      })
      .map((doc) => {
        const invoiceAmount = Number(doc?.grandTotal || 0);
        const paidAmount = Number(doc?.paidAmount || 0);
        const balanceAmount = Math.max(
          0,
          Number(
            doc?.balanceAmount !== undefined && doc?.balanceAmount !== null
              ? doc.balanceAmount
              : invoiceAmount - paidAmount,
          ),
        );

        const paymentStatus =
          invoiceAmount > 0 && balanceAmount <= 0 && paidAmount > 0
            ? "paid"
            : paidAmount > 0
              ? "partially_paid"
              : "pending";

        const paymentList = Array.isArray(doc?.payments) ? doc.payments : [];
        const latestPayment = paymentList.length
          ? paymentList[paymentList.length - 1]
          : null;

        return {
          _id: `invoice-${doc._id}`,
          type: "income",
          date: doc.documentDate || doc.createdAt || todayDate(),
          amount: invoiceAmount,
          totalAmount: invoiceAmount,
          company: doc.company || selectedCompany,
          bankAccount: latestPayment?.senderBankName || "",
          paymentMode: latestPayment?.paymentMethod || "",
          projectName: doc.projectName || "",
          partyName:
            doc.client?.companyName || doc.client?.name || doc.clientName || "",
          officeUnit: "",
          head: "",
          subHead: "",
          groupName: "",
          classificationType: "",
          classification: "",
          isUnmapped: true,
          locked: false,
          status: "pending",
          invoiceNo: doc.number || doc.invoiceNumber || "",
          gstNo: String(doc.clientGstin || "")
            .trim()
            .toUpperCase(),
          invoiceDocumentId: doc._id || "",
          isInvoiceGeneratedCard: true,
          invoiceAmount,
          paidAmount,
          balanceAmount,
          paymentStatus,
          payments: paymentList,
          note: doc.subject || "Automatically linked from Invoice",
        };
      });
  }, [salesDocuments, entries, selectedCompany]);

  const invoiceCardsForSelectedPeriod = useMemo(() => {
    return invoiceFinanceEntries.filter((entry) => {
      if (!entry?.date) return false;

      const d = new Date(entry.date);
      if (Number.isNaN(d.getTime())) return false;

      const financialYearStart = Number(
        dateFilter.financialYear || getCurrentFinancialYearStart(),
      );
      const month = Number(dateFilter.month || 4);
      const quarter = Number(dateFilter.quarter || 1);

      if (dateFilter.mode === "month") {
        const calendarYear = getCalendarYearForFinancialMonth(
          financialYearStart,
          month,
        );
        return d.getFullYear() === calendarYear && d.getMonth() + 1 === month;
      }

      if (dateFilter.mode === "quarter") {
        const quarterRanges = {
          1: {
            from: new Date(financialYearStart, 3, 1),
            to: new Date(financialYearStart, 5, 30, 23, 59, 59, 999),
          },
          2: {
            from: new Date(financialYearStart, 6, 1),
            to: new Date(financialYearStart, 8, 30, 23, 59, 59, 999),
          },
          3: {
            from: new Date(financialYearStart, 9, 1),
            to: new Date(financialYearStart, 11, 31, 23, 59, 59, 999),
          },
          4: {
            from: new Date(financialYearStart + 1, 0, 1),
            to: new Date(financialYearStart + 1, 2, 31, 23, 59, 59, 999),
          },
        };

        const range = quarterRanges[quarter] || quarterRanges[1];
        return d >= range.from && d <= range.to;
      }

      if (dateFilter.mode === "year") {
        const from = new Date(financialYearStart, 3, 1);
        const to = new Date(financialYearStart + 1, 2, 31, 23, 59, 59, 999);
        return d >= from && d <= to;
      }

      if (dateFilter.mode === "custom") {
        const from = dateFilter.fromDate
          ? new Date(`${dateFilter.fromDate}T00:00:00`)
          : null;
        const to = dateFilter.toDate
          ? new Date(`${dateFilter.toDate}T23:59:59.999`)
          : null;

        if (from && d < from) return false;
        if (to && d > to) return false;
      }

      return true;
    });
  }, [invoiceFinanceEntries, dateFilter]);

  const invoiceDocMap = useMemo(() => {
    const map = new Map();

    salesDocuments
      .filter((doc) => String(doc?.type || "").toLowerCase() === "invoice")
      .forEach((doc) => {
        if (doc?._id) map.set(`id:${String(doc._id)}`, doc);

        const no = String(doc?.number || doc?.invoiceNumber || "")
          .trim()
          .toLowerCase();
        if (no) map.set(`no:${no}`, doc);
      });

    return map;
  }, [salesDocuments]);

  const getLinkedInvoiceDocument = (entry) => {
    const invoiceId = String(
      entry?.invoiceDocumentId?._id || entry?.invoiceDocumentId || "",
    );
    const invoiceNo = String(entry?.invoiceNo || "")
      .trim()
      .toLowerCase();

    return (
      (invoiceId && invoiceDocMap.get(`id:${invoiceId}`)) ||
      (invoiceNo && invoiceDocMap.get(`no:${invoiceNo}`)) ||
      null
    );
  };

  const enrichFinanceEntryWithInvoice = (entry) => {
    const invoiceId = String(
      entry?.invoiceDocumentId?._id || entry?.invoiceDocumentId || "",
    );
    const invoiceNo = String(entry?.invoiceNo || "")
      .trim()
      .toLowerCase();

    const doc =
      (invoiceId && invoiceDocMap.get(`id:${invoiceId}`)) ||
      (invoiceNo && invoiceDocMap.get(`no:${invoiceNo}`));

    if (!doc) return entry;

    const invoiceAmount = Number(doc.grandTotal || entry.totalAmount || 0);
    const paidAmount = Number(doc.paidAmount || 0);
    const balanceAmount = Math.max(
      0,
      Number(
        doc.balanceAmount !== undefined && doc.balanceAmount !== null
          ? doc.balanceAmount
          : invoiceAmount - paidAmount,
      ),
    );

    const paymentStatus =
      invoiceAmount > 0 && balanceAmount <= 0 && paidAmount > 0
        ? "paid"
        : paidAmount > 0
          ? "partially_paid"
          : "pending";

    const paymentList = Array.isArray(doc.payments) ? doc.payments : [];
    const latestPayment = paymentList.length
      ? paymentList[paymentList.length - 1]
      : null;

    return {
      ...entry,
      invoiceAmount,
      paidAmount,
      balanceAmount,
      paymentStatus,
      payments: paymentList,
      paymentMode: latestPayment?.paymentMethod || entry.paymentMode || "",
      bankAccount: latestPayment?.senderBankName || entry.bankAccount || "",
      invoiceDocumentId: doc._id || entry.invoiceDocumentId,
      invoiceNo: doc.number || doc.invoiceNumber || entry.invoiceNo,
      gstNo: String(doc.clientGstin || "")
        .trim()
        .toUpperCase(),
      projectName: entry.projectName || doc.projectName || "",
      partyName:
        entry.partyName ||
        doc.client?.companyName ||
        doc.client?.name ||
        doc.clientName ||
        "",
    };
  };

  // Visible cards according to each column's own filter.
  // Invoice-only cards are display cards and are NOT included in Cash Flow / P&L totals.
  const actualIncomeEntries = filterByLockStatus(
    allIncomeEntries.map(enrichFinanceEntryWithInvoice),
    incomeLockFilter,
  );

  const invoiceOnlyVisibleEntries =
    incomeLockFilter === "locked"
      ? []
      : invoiceCardsForSelectedPeriod.filter((entry) =>
          matchesFinanceFilter(
            entry,
            incomeFilterType,
            incomeFilterValue,
            "income",
          ),
        );

  const incomeEntries = sortUnlockedFirst([
    ...actualIncomeEntries,
    ...invoiceOnlyVisibleEntries,
  ]);

  const expenseEntries = filterByLockStatus(
    allExpenseEntries,
    expenseLockFilter,
  );

  // Amount displayed beside each Locked / Unlocked / All filter.
  // Uses totalAmount when available, otherwise falls back to amount.
  const visibleIncomeAmount = actualIncomeEntries.reduce(
    (sum, entry) => sum + Number(entry.totalAmount ?? entry.amount ?? 0),
    0,
  );

  const visibleExpenseAmount = expenseEntries.reduce(
    (sum, entry) => sum + Number(entry.totalAmount ?? entry.amount ?? 0),
    0,
  );

  const previewAmount = buildAmountValues(form);

  const totalInflow = allIncomeEntries.reduce((sum, e) => {
    const linkedInvoice = getLinkedInvoiceDocument(e);

    if (linkedInvoice) {
      return sum + Number(linkedInvoice.paidAmount || 0);
    }

    return sum + Number(e.totalAmount || e.amount || 0);
  }, 0);
  const totalOutflow = allExpenseEntries.reduce(
    (sum, e) => sum + Number(e.totalAmount || e.amount || 0),
    0,
  );
  const cashFlow = totalInflow - totalOutflow;

  const pnlIncome = allIncomeEntries
    .filter((e) => !["asset", "liability"].includes(e.classificationType))
    .reduce((sum, e) => {
      const linkedInvoice = getLinkedInvoiceDocument(e);

      if (linkedInvoice) {
        return sum + Number(linkedInvoice.paidAmount || 0);
      }

      return sum + Number(e.totalAmount || e.amount || 0);
    }, 0);

  const pnlExpense = allExpenseEntries
    .filter((e) => !["asset", "liability"].includes(e.classificationType))
    .reduce((sum, e) => sum + Number(e.totalAmount || e.amount || 0), 0);

  const profitLoss = pnlIncome - pnlExpense;

  const maxInflowEntry = allIncomeEntries.reduce(
    (max, e) =>
      Number(e.totalAmount || e.amount || 0) >
      Number(max?.totalAmount || max?.amount || 0)
        ? e
        : max,
    null,
  );

  const maxOutflowEntry = allExpenseEntries.reduce(
    (max, e) =>
      Number(e.totalAmount || e.amount || 0) >
      Number(max?.totalAmount || max?.amount || 0)
        ? e
        : max,
    null,
  );

  const formatMoney = (value) =>
    `₹ ${Number(value || 0).toLocaleString("en-IN", {
      maximumFractionDigits: 2,
    })}`;

  const getUniqueFilterValues = (items) =>
    Array.from(
      new Set(
        (Array.isArray(items) ? items : [])
          .map((item) =>
            typeof item === "string"
              ? item
              : item?.name || item?.title || item?.label || item?.value || "",
          )
          .map((item) => String(item || "").trim())
          .filter(Boolean),
      ),
    ).sort((a, b) => a.localeCompare(b, undefined, { sensitivity: "base" }));

  const getFilterValuesByType = (filterType) => {
    switch (filterType) {
      case "project":
        return getUniqueFilterValues(
          projects.map((project) => project?.name || ""),
        );
      case "officeUnit":
        return getUniqueFilterValues(masters.officeUnits);
      case "customer":
        return getUniqueFilterValues(masters.customers);
      case "incomeHead":
        return getUniqueFilterValues(masters.incomeHeads);
      case "subincomeHead":
        return getUniqueFilterValues(masters.subincomeHeads);
      case "incomeGroup":
        return getUniqueFilterValues(masters.incomeGroups);
      case "vendor":
        return getUniqueFilterValues(masters.vendors);
      case "expenseHead":
        return getUniqueFilterValues(masters.expenseHeads);
      case "subexpenseHead":
        return getUniqueFilterValues(masters.subexpenseHeads);
      case "expenseGroup":
        return getUniqueFilterValues(masters.expenseGroups);
      case "asset":
        return getUniqueFilterValues(masters.assets);
      case "liability":
        return getUniqueFilterValues(masters.liabilities);
      default:
        return [];
    }
  };

  const filterLabelMap = {
    project: "Project Select",
    officeUnit: "Office Unit",
    customer: "Customer Name",
    incomeHead: "Income Parent Head",
    subincomeHead: "Income Sub Head",
    incomeGroup: "Income Group",
    vendor: "Vendor Name",
    expenseHead: "Expense Parent Head",
    subexpenseHead: "Expense Sub Head",
    expenseGroup: "Expense Group",
    asset: "Asset",
    liability: "Liability",
  };

  const incomeFilterOptions = useMemo(
    () => getFilterValuesByType(incomeFilterType),
    [incomeFilterType, projects, masters],
  );

  const expenseFilterOptions = useMemo(
    () => getFilterValuesByType(expenseFilterType),
    [expenseFilterType, projects, masters],
  );

  const incomeFilterLabel =
    filterLabelMap[incomeFilterType] || "Income Filter Value";

  const expenseFilterLabel =
    filterLabelMap[expenseFilterType] || "Expense Filter Value";

  return (
    <div className="finance-page">
      <div className="finance-top">
        <div className="finance-top-title">
          <h2>Income & Expense Entry</h2>
        </div>

        <section className="finance-summary-grid finance-summary-header">
          <div className="finance-summary-card">
            <span>Cash Flow</span>
            <strong>{formatMoney(cashFlow)}</strong>
            <small>Total Inflow - Total Outflow</small>
          </div>

          <div className="finance-summary-card">
            <span>P &amp; L</span>
            <strong>{formatMoney(profitLoss)}</strong>
            <small>Operating Income - Expense (Asset/Liability excluded)</small>
          </div>

          <div className="finance-summary-card">
            <span>Max Inflow</span>
            <strong>
              {formatMoney(
                maxInflowEntry?.totalAmount || maxInflowEntry?.amount,
              )}
            </strong>
            <small>{maxInflowEntry?.projectName || "Not mapped"}</small>
          </div>

          <div className="finance-summary-card">
            <span>Max Outflow</span>
            <strong>
              {formatMoney(
                maxOutflowEntry?.totalAmount || maxOutflowEntry?.amount,
              )}
            </strong>
            <small>{maxOutflowEntry?.projectName || "Not mapped"}</small>
          </div>
        </section>
        <button
          type="button"
          className="finance-back-button"
          onClick={() => window.history.back()}
          title="Back to previous page"
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: "7px",
            marginBottom: "8px",
            padding: "7px 12px",
            border: "1px solid #d7dee8",
            borderRadius: "7px",
            background: "#ffffff",
            color: "#334155",
            fontSize: "13px",
            fontWeight: 700,
            cursor: "pointer",
            boxShadow: "0 1px 3px rgba(15, 23, 42, 0.06)",
          }}
        >
          <span aria-hidden="true" style={{ fontSize: "17px", lineHeight: 1 }}>
            ←
          </span>
          Back
        </button>
      </div>

      <div className="finance-top-filter-split">
        <section className="finance-filter-card">
          <div className="finance-filter-header">
            <div>
              <h3>Financial Period</h3>
            </div>

            <button
              type="button"
              className="finance-filter-clear"
              onClick={clearDateFilter}
            >
              Reset
            </button>
          </div>

          <div className="finance-filter-row">
            <label>
              Filter By
              <select
                value={dateFilter.mode}
                onChange={(e) =>
                  setDateFilter((prev) => ({ ...prev, mode: e.target.value }))
                }
              >
                <option value="month">Month Wise</option>
                <option value="quarter">Quarterly</option>
                <option value="year">Year Wise</option>
                <option value="custom">Custom Range</option>
              </select>
            </label>

            {(dateFilter.mode === "month" ||
              dateFilter.mode === "quarter" ||
              dateFilter.mode === "year") && (
              <label>
                Financial Year
                <select
                  value={dateFilter.financialYear}
                  onChange={(e) =>
                    setDateFilter((prev) => ({
                      ...prev,
                      financialYear: e.target.value,
                    }))
                  }
                >
                  {availableFinancialYears.map((year) => (
                    <option key={year} value={year}>
                      {getFinancialYearLabel(year)}
                    </option>
                  ))}
                </select>
              </label>
            )}

            {dateFilter.mode === "month" && (
              <label>
                Month
                <select
                  value={dateFilter.month}
                  onChange={(e) =>
                    setDateFilter((prev) => ({
                      ...prev,
                      month: e.target.value,
                    }))
                  }
                >
                  <option value="1">January</option>
                  <option value="2">February</option>
                  <option value="3">March</option>
                  <option value="4">April</option>
                  <option value="5">May</option>
                  <option value="6">June</option>
                  <option value="7">July</option>
                  <option value="8">August</option>
                  <option value="9">September</option>
                  <option value="10">October</option>
                  <option value="11">November</option>
                  <option value="12">December</option>
                </select>
              </label>
            )}

            {dateFilter.mode === "quarter" && (
              <label>
                Quarter
                <select
                  value={dateFilter.quarter}
                  onChange={(e) =>
                    setDateFilter((prev) => ({
                      ...prev,
                      quarter: e.target.value,
                    }))
                  }
                >
                  <option value="1">Q1 (Apr - Jun)</option>
                  <option value="2">Q2 (Jul - Sep)</option>
                  <option value="3">Q3 (Oct - Dec)</option>
                  <option value="4">Q4 (Jan - Mar)</option>
                </select>
              </label>
            )}

            {dateFilter.mode === "custom" && (
              <>
                <label>
                  From Date
                  <input
                    type="date"
                    value={dateFilter.fromDate}
                    onChange={(e) =>
                      setDateFilter((prev) => ({
                        ...prev,
                        fromDate: e.target.value,
                      }))
                    }
                  />
                </label>

                <label>
                  To Date
                  <input
                    type="date"
                    min={dateFilter.fromDate || undefined}
                    value={dateFilter.toDate}
                    onChange={(e) =>
                      setDateFilter((prev) => ({
                        ...prev,
                        toDate: e.target.value,
                      }))
                    }
                  />
                </label>
              </>
            )}

            <div className="finance-filter-count">
              <span>Showing</span>
              <b>{filteredEntries.length}</b>
              <small>of {entries.length} entries</small>
            </div>
          </div>
        </section>

        <section className="finance-head-filter-card">
          <div className="finance-head-filter-header">
            <h3>Heads</h3>

            <button
              type="button"
              className="finance-head-filter-reset"
              onClick={() => {
                setIncomeFilterType("all");
                setIncomeFilterValue("all");
                setExpenseFilterType("all");
                setExpenseFilterValue("all");
              }}
            >
              Reset
            </button>
          </div>

          <div className="finance-head-filter-sides">
            <div className="finance-filter-side income-filter-side">
              <div className="finance-filter-side-title">Income</div>

              <div className="finance-filter-side-row">
                <label>
                  Head
                  <select
                    value={incomeFilterType}
                    onChange={(e) => {
                      setIncomeFilterType(e.target.value);
                      setIncomeFilterValue("all");
                    }}
                  >
                    <option value="all">All</option>
                    <option value="project">Project Select</option>
                    <option value="officeUnit">Office Unit</option>
                    <option value="customer">Customer Name</option>
                    <option value="incomeHead">Income Parent Head</option>
                    <option value="subincomeHead">Income Sub Head</option>
                    <option value="incomeGroup">Income Group</option>
                    <option value="asset">Asset</option>
                    <option value="liability">Liability</option>
                  </select>
                </label>

                <label>
                  Value
                  <select
                    value={incomeFilterValue}
                    disabled={incomeFilterType === "all"}
                    onChange={(e) => setIncomeFilterValue(e.target.value)}
                  >
                    <option value="all">
                      {incomeFilterType === "all"
                        ? "Select Head"
                        : `All ${incomeFilterLabel}`}
                    </option>

                    {incomeFilterOptions.map((value) => (
                      <option
                        key={`income-${incomeFilterType}-${value}`}
                        value={value}
                      >
                        {value}
                      </option>
                    ))}
                  </select>
                </label>
              </div>
            </div>

            <div className="finance-filter-side expense-filter-side">
              <div className="finance-filter-side-title">Expense</div>

              <div className="finance-filter-side-row">
                <label>
                  Head
                  <select
                    value={expenseFilterType}
                    onChange={(e) => {
                      setExpenseFilterType(e.target.value);
                      setExpenseFilterValue("all");
                    }}
                  >
                    <option value="all">All</option>
                    <option value="project">Project Select</option>
                    <option value="officeUnit">Office Unit</option>
                    <option value="vendor">Vendor Name</option>
                    <option value="expenseHead">Expense Parent Head</option>
                    <option value="subexpenseHead">Expense Sub Head</option>
                    <option value="expenseGroup">Expense Group</option>
                    <option value="asset">Asset</option>
                    <option value="liability">Liability</option>
                  </select>
                </label>

                <label>
                  Value
                  <select
                    value={expenseFilterValue}
                    disabled={expenseFilterType === "all"}
                    onChange={(e) => setExpenseFilterValue(e.target.value)}
                  >
                    <option value="all">
                      {expenseFilterType === "all"
                        ? "Select Head"
                        : `All ${expenseFilterLabel}`}
                    </option>

                    {expenseFilterOptions.map((value) => (
                      <option
                        key={`expense-${expenseFilterType}-${value}`}
                        value={value}
                      >
                        {value}
                      </option>
                    ))}
                  </select>
                </label>
              </div>
            </div>
          </div>
        </section>
      </div>

      <div className="finance-main-grid">
        <aside className="finance-side-panel finance-master-panel income-master-panel">
          <SelectDragBox
            title="Project Select"
            color="green"
            items={projects.map((p) => ({
              label: p.name,
              value: p._id || p.id || p.projectId || p.name,
              raw: p,
            }))}
            emptyText="No projects found"
            getData={(p) => ({
              type: "project",
              id: p._id || p.id || p.projectId || null,
              value: p.name,
            })}
          />
          <SelectDragBox
            title="Office Unit"
            color="green"
            add={() => addMaster("officeUnits")}
            items={masters.officeUnits.map((x) => ({
              label: x,
              value: x,
              raw: x,
            }))}
            emptyText="No office unit added"
            getData={(x) => ({ type: "officeUnit", value: x })}
          />

          <SelectDragBox
            title="Income Parent Head"
            color="green"
            add={() => addMaster("incomeHeads")}
            items={masters.incomeHeads.map((x) => ({
              label: x,
              value: x,
              raw: x,
            }))}
            emptyText="No income head added"
            getData={(x) => ({ type: "incomeHead", value: x })}
          />
          <SelectDragBox
            title="Income Sub Head"
            color="green"
            add={() => addMaster("subincomeHeads")}
            items={masters.subincomeHeads.map((x) => ({
              label: x,
              value: x,
              raw: x,
            }))}
            emptyText="No sub income head added"
            getData={(x) => ({ type: "subincomeHead", value: x })}
          />

          <SelectDragBox
            title="Income Group"
            color="green"
            add={() => addMaster("incomeGroups")}
            items={masters.incomeGroups.map((x) => ({
              label: x,
              value: x,
              raw: x,
            }))}
            emptyText="No income group added"
            getData={(x) => ({ type: "incomeGroup", value: x })}
          />

          <SelectDragBox
            title="Asset"
            color="green"
            add={() => addMaster("assets")}
            items={masters.assets.map((x) => ({
              label: x,
              value: x,
              raw: x,
            }))}
            emptyText="No asset added"
            getData={(x) => ({ type: "asset", value: x })}
          />

          <SelectDragBox
            title="Liability"
            color="green"
            add={() => addMaster("liabilities")}
            items={masters.liabilities.map((x) => ({
              label: x,
              value: x,
              raw: x,
            }))}
            emptyText="No liability added"
            getData={(x) => ({ type: "liability", value: x })}
          />
        </aside>

        <main className="finance-entries-area">
          <section className="finance-column income-column">
            <div className="finance-column-header">
              <div>
                <h3>In Flow</h3>
              </div>

              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "flex-end",
                  gap: "8px",
                  flexWrap: "wrap",
                }}
              >
                <select
                  value={incomeLockFilter}
                  onChange={(e) => setIncomeLockFilter(e.target.value)}
                  title="Filter In Flow entries by lock status"
                  style={{
                    height: "36px",
                    minWidth: "112px",
                    padding: "0 30px 0 11px",
                    border: "1px solid #cbd5e1",
                    borderRadius: "8px",
                    background: "#ffffff",
                    color: "#334155",
                    fontSize: "12px",
                    fontWeight: 700,
                    cursor: "pointer",
                    outline: "none",
                  }}
                >
                  <option value="all">All</option>
                  <option value="unlocked">Unlocked</option>
                  <option value="locked">Locked</option>
                </select>

                <div
                  title={`${incomeEntries.length} visible In Flow entr${
                    incomeEntries.length === 1 ? "y" : "ies"
                  }`}
                  style={{
                    height: "36px",
                    minWidth: "112px",
                    padding: "0 12px",
                    border: "1px solid #bbf7d0",
                    borderRadius: "8px",
                    background: "#f0fdf4",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: "6px",
                    whiteSpace: "nowrap",
                  }}
                >
                  <span
                    style={{
                      color: "#64748b",
                      fontSize: "10px",
                      fontWeight: 800,
                      textTransform: "uppercase",
                      letterSpacing: "0.04em",
                    }}
                  >
                    {incomeEntries.length}
                  </span>
                  <strong
                    style={{
                      color: "#166534",
                      fontSize: "13px",
                      fontWeight: 800,
                    }}
                  >
                    {formatMoney(visibleIncomeAmount)}
                  </strong>
                </div>

                <button
                  className="finance-btn income-btn"
                  onClick={() => openPopup("income")}
                >
                  + Add In Flow
                </button>
              </div>
            </div>

            {incomeEntries.length === 0 && (
              <div className="finance-empty-box">No income entries found</div>
            )}

            {incomeEntries.map((e) => (
              <EntryCard
                key={e._id}
                entry={normalizeEntry(e)}
                type="income"
                canLock={canLock}
                onLock={lockEntry}
                onEdit={openEditPopup}
                onRecordPayment={openInvoicePaymentPopup}
                onDropToCard={updateEntryWithDraggedData}
                onOpenSalesDocument={openSalesDocumentPdf}
              />
            ))}
          </section>

          <section className="finance-column expense-column">
            <div className="finance-column-header">
              <div>
                <h3>Out Flow</h3>
              </div>

              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "flex-end",
                  gap: "8px",
                  flexWrap: "wrap",
                }}
              >
                <select
                  value={expenseLockFilter}
                  onChange={(e) => setExpenseLockFilter(e.target.value)}
                  title="Filter Out Flow entries by lock status"
                  style={{
                    height: "36px",
                    minWidth: "112px",
                    padding: "0 30px 0 11px",
                    border: "1px solid #cbd5e1",
                    borderRadius: "8px",
                    background: "#ffffff",
                    color: "#334155",
                    fontSize: "12px",
                    fontWeight: 700,
                    cursor: "pointer",
                    outline: "none",
                  }}
                >
                  <option value="all">All</option>
                  <option value="unlocked">Unlocked</option>
                  <option value="locked">Locked</option>
                </select>

                <div
                  title={`${expenseEntries.length} visible Out Flow entr${
                    expenseEntries.length === 1 ? "y" : "ies"
                  }`}
                  style={{
                    height: "36px",
                    minWidth: "112px",
                    padding: "0 12px",
                    border: "1px solid #fecaca",
                    borderRadius: "8px",
                    background: "#fef2f2",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: "6px",
                    whiteSpace: "nowrap",
                  }}
                >
                  <span
                    style={{
                      color: "#64748b",
                      fontSize: "10px",
                      fontWeight: 800,
                      textTransform: "uppercase",
                      letterSpacing: "0.04em",
                    }}
                  >
                    {expenseEntries.length}
                  </span>
                  <strong
                    style={{
                      color: "#b91c1c",
                      fontSize: "13px",
                      fontWeight: 800,
                    }}
                  >
                    {formatMoney(visibleExpenseAmount)}
                  </strong>
                </div>

                <button
                  className="finance-btn expense-btn"
                  onClick={() => openPopup("expense")}
                >
                  + Add Out Flow
                </button>
              </div>
            </div>

            {expenseEntries.length === 0 && (
              <div className="finance-empty-box">No expense entries found</div>
            )}

            {expenseEntries.map((e) => (
              <EntryCard
                key={e._id}
                entry={normalizeEntry(e)}
                type="expense"
                canLock={canLock}
                onLock={lockEntry}
                onEdit={openEditPopup}
                onRecordPayment={openInvoicePaymentPopup}
                onDropToCard={updateEntryWithDraggedData}
                onOpenSalesDocument={openSalesDocumentPdf}
                onOpenPurchaseInvoice={openPurchaseInvoice}
                onOpenPurchaseDocument={openPurchaseDocument}
              />
            ))}
          </section>
        </main>

        <aside className="finance-side-panel finance-master-panel expense-master-panel">
          <SelectDragBox
            title="Project Select"
            color="red"
            items={projects.map((p) => ({
              label: p.name,
              value: p._id || p.id || p.projectId || p.name,
              raw: p,
            }))}
            emptyText="No projects found"
            getData={(p) => ({
              type: "project",
              id: p._id || p.id || p.projectId || null,
              value: p.name,
            })}
          />
          <SelectDragBox
            title="Office Unit"
            color="red"
            add={() => addMaster("officeUnits")}
            items={masters.officeUnits.map((x) => ({
              label: x,
              value: x,
              raw: x,
            }))}
            emptyText="No office unit added"
            getData={(x) => ({ type: "officeUnit", value: x })}
          />

          <SelectDragBox
            title="Vendor Name"
            color="red"
            add={() => addMaster("vendors")}
            items={masters.vendors.map((x) => ({ label: x, value: x, raw: x }))}
            emptyText="No vendor added"
            getData={(x) => ({ type: "vendor", value: x })}
          />

          <SelectDragBox
            title="Expense Parent Head"
            color="red"
            add={() => addMaster("expenseHeads")}
            items={masters.expenseHeads.map((x) => ({
              label: x,
              value: x,
              raw: x,
            }))}
            emptyText="No expense head added"
            getData={(x) => ({ type: "expenseHead", value: x })}
          />

          <SelectDragBox
            title="Expense Sub Head"
            color="red"
            add={() => addMaster("subexpenseHeads")}
            items={masters.subexpenseHeads.map((x) => ({
              label: x,
              value: x,
              raw: x,
            }))}
            emptyText="No expense sub head added"
            getData={(x) => ({ type: "subexpenseHead", value: x })}
          />

          <SelectDragBox
            title="Expense Group"
            color="red"
            add={() => addMaster("expenseGroups")}
            items={masters.expenseGroups.map((x) => ({
              label: x,
              value: x,
              raw: x,
            }))}
            emptyText="No expense group added"
            getData={(x) => ({ type: "expenseGroup", value: x })}
          />

          <SelectDragBox
            title="Asset"
            color="red"
            add={() => addMaster("assets")}
            items={masters.assets.map((x) => ({
              label: x,
              value: x,
              raw: x,
            }))}
            emptyText="No asset added"
            getData={(x) => ({ type: "asset", value: x })}
          />

          <SelectDragBox
            title="Liability"
            color="red"
            add={() => addMaster("liabilities")}
            items={masters.liabilities.map((x) => ({
              label: x,
              value: x,
              raw: x,
            }))}
            emptyText="No liability added"
            getData={(x) => ({ type: "liability", value: x })}
          />
        </aside>
      </div>

      {popupOpen && (
        <div className="finance-overlay">
          <div className="finance-popup finance-entry-modal">
            <div className="finance-modal-header">
              <h3>
                {editingEntryId
                  ? entryType === "income"
                    ? "Edit Incoming Entry"
                    : "Edit Outgoing Entry"
                  : entryType === "income"
                    ? "Add Incoming Entry"
                    : "Add Outgoing Entry"}
              </h3>

              <div className="finance-modal-company">
                <span>Company</span>
                <select
                  name="company"
                  value={form.company}
                  onChange={handleChange}
                >
                  <option value="SDC">SDC</option>
                </select>
              </div>
            </div>

            <div className="finance-entry-basic-row">
              <label>
                Date
                <input
                  type="date"
                  name="date"
                  value={form.date}
                  onChange={handleChange}
                />
              </label>

              <label>
                Amount
                <input
                  type="number"
                  name="amount"
                  placeholder="Amount"
                  value={form.amount}
                  onChange={handleChange}
                />
              </label>
            </div>

            <div
              className={`finance-payment-row ${
                form.paymentMode === "Cash" ? "cash-mode" : ""
              }`}
            >
              <label>
                Payment Mode
                <select
                  name="paymentMode"
                  value={form.paymentMode}
                  onChange={handleChange}
                >
                  {PAYMENT_MODES.map((mode) => (
                    <option key={mode} value={mode}>
                      {mode}
                    </option>
                  ))}
                </select>
              </label>

              {form.paymentMode !== "Cash" && (
                <>
                  <label>
                    Bank
                    <select
                      name="bankAccount"
                      value={form.bankAccount}
                      onChange={handleChange}
                    >
                      {(COMPANY_BANKS[form.company] || []).map((bank) => (
                        <option key={bank} value={bank}>
                          {bank}
                        </option>
                      ))}
                    </select>
                  </label>

                  <label>
                    GST %
                    <select
                      name="gstPercent"
                      value={form.gstPercent}
                      onChange={handleChange}
                    >
                      <option value="">No GST</option>
                      <option value="5">5%</option>
                      <option value="12">12%</option>
                      <option value="18">18%</option>
                    </select>
                  </label>
                  <label className="finance-field">
                    <span>GST No.</span>
                    <input
                      type="text"
                      name="gstNo"
                      value={form.gstNo || ""}
                      onChange={handleChange}
                      placeholder="Enter GST Number"
                      maxLength={15}
                      style={{ textTransform: "uppercase" }}
                    />
                  </label>
                  <label>
                    TDS %
                    <input
                      type="number"
                      name="tdsPercent"
                      placeholder="TDS %"
                      value={form.tdsPercent}
                      onChange={handleChange}
                    />
                  </label>
                  <div className="finance-tds-single-row">
                    <button
                      type="button"
                      className={`finance-tds-btn ${form.tdsSettlementOpen ? "active" : ""}`}
                      onClick={() =>
                        setForm((p) => ({
                          ...p,
                          tdsSettlementOpen: !p.tdsSettlementOpen,
                        }))
                      }
                    >
                      {entryType === "income" ? "Received TDS" : "Paid TDS"}
                      {form.tdsSettlementOpen ? " −" : " +"}
                    </button>

                    {form.tdsSettlementOpen && (
                      <>
                        <label className="finance-tds-amount-field">
                          TDS Amount
                          <input
                            type="number"
                            min="0"
                            name="tdsSettlementAmount"
                            value={form.tdsSettlementAmount}
                            onChange={handleChange}
                            placeholder="Enter amount"
                          />
                        </label>
                        <div className="finance-tds-proof-field">
                          <span className="finance-tds-field-title">
                            TDS Proof
                          </span>

                          <input
                            id="finance-tds-proof-upload"
                            className="finance-tds-hidden-input"
                            type="file"
                            accept=".pdf,image/*"
                            onChange={(e) => {
                              handleFileChange(
                                "tdsProofFile",
                                e.target.files?.[0],
                              );
                            }}
                          />

                          <div className="finance-tds-proof-actions">
                            {form.tdsProofFile ||
                            form.tdsProofExistingFileName ? (
                              <>
                                {form.tdsProofFile && (
                                  <button
                                    type="button"
                                    className="finance-tds-view-btn"
                                    onClick={() => {
                                      const url = URL.createObjectURL(
                                        form.tdsProofFile,
                                      );
                                      window.open(
                                        url,
                                        "_blank",
                                        "noopener,noreferrer",
                                      );
                                    }}
                                  >
                                    View
                                  </button>
                                )}

                                <label
                                  htmlFor="finance-tds-proof-upload"
                                  className="finance-tds-upload-btn"
                                >
                                  Change
                                </label>
                              </>
                            ) : (
                              <label
                                htmlFor="finance-tds-proof-upload"
                                className="finance-tds-upload-btn"
                              >
                                + Attach Proof
                              </label>
                            )}
                          </div>

                          {(form.tdsProofFile ||
                            form.tdsProofExistingFileName) && (
                            <small className="finance-tds-file-name"></small>
                          )}
                        </div>
                      </>
                    )}
                  </div>
                </>
              )}
            </div>

            <div className="finance-document-row">
              <OtherDocumentUpload
                file={form.otherDocFile}
                onFileChange={(file) => handleFileChange("otherDocFile", file)}
              />

              <DocumentReferenceField
                title="Quotation"
                shortTitle="Quotation"
                inputId="finance-quote-file"
                documentValue={form.quoteDocumentId}
                documents={getDocumentsByType("quotation")}
                existingFiles={getExistingAttachedFiles("quotation")}
                attachedFileName={
                  form.quoteFile?.name || form.quoteExistingFileName
                }
                loading={documentsLoading}
                onDocumentChange={(value) =>
                  handleDocumentSelect("quotation", value)
                }
                file={form.quoteFile}
                onFileChange={(file) => handleFileChange("quoteFile", file)}
              />

              <DocumentReferenceField
                title="Proforma Invoice (PI)"
                shortTitle="PI"
                inputId="finance-proforma-file"
                documentValue={form.proformaDocumentId}
                documents={getDocumentsByType("proforma")}
                existingFiles={getExistingAttachedFiles("proforma")}
                attachedFileName={
                  form.proformaFile?.name || form.proformaExistingFileName
                }
                loading={documentsLoading}
                onDocumentChange={(value) =>
                  handleDocumentSelect("proforma", value)
                }
                file={form.proformaFile}
                onFileChange={(file) => handleFileChange("proformaFile", file)}
              />

              <DocumentReferenceField
                title="Bill / Invoice"
                shortTitle="Invoice"
                inputId="finance-invoice-file"
                documentValue={form.invoiceDocumentId}
                documents={getDocumentsByType("invoice")}
                existingFiles={getExistingAttachedFiles("invoice")}
                attachedFileName={
                  form.invoiceFile?.name || form.invoiceExistingFileName
                }
                loading={documentsLoading}
                onDocumentChange={(value) =>
                  handleDocumentSelect("invoice", value)
                }
                file={form.invoiceFile}
                onFileChange={(file) => handleFileChange("invoiceFile", file)}
              />
            </div>

            <label className="finance-narration-field">
              Note / Narration
              <textarea
                name="note"
                placeholder="Add note / narration"
                value={form.note}
                maxLength={500}
                onChange={handleChange}
                rows="3"
              />
              <small>{String(form.note || "").length} / 500</small>
            </label>

            <div className="amount-preview-card finance-modal-preview">
              <div>
                <span>Base Amount</span>
                <b>₹ {previewAmount.amount.toLocaleString("en-IN")}</b>
              </div>
              <div>
                <span>GST</span>
                <b>₹ {previewAmount.gstAmount.toLocaleString("en-IN")}</b>
              </div>
              <div>
                <span>TDS</span>
                <b>₹ {previewAmount.tdsAmount.toLocaleString("en-IN")}</b>
              </div>
              <div className="preview-total">
                <span>Total</span>
                <b>₹ {previewAmount.totalAmount.toLocaleString("en-IN")}</b>
              </div>
            </div>

            <div className="finance-popup-footer">
              <button className="cancel-btn" onClick={closePopup}>
                Cancel
              </button>

              <button
                className={`finance-btn ${
                  entryType === "income" ? "income-btn" : "expense-btn"
                }`}
                onClick={saveEntry}
                disabled={loading}
              >
                {loading
                  ? "Saving..."
                  : editingEntryId
                    ? "Update Entry"
                    : "Save for Review"}
              </button>
            </div>
          </div>
        </div>
      )}

      {invoicePaymentOpen && invoicePaymentDoc && (
        <div className="finance-overlay" style={{ zIndex: 9999 }}>
          <div className="finance-popup finance-entry-modal invoice-payment-modal">
            <div className="finance-modal-header">
              <div>
                <h3>Record Invoice Payment</h3>
                <div style={{ marginTop: 4, fontSize: 12, color: "#64748b" }}>
                  Invoice:{" "}
                  <b>
                    {invoicePaymentDoc.number ||
                      invoicePaymentDoc.invoiceNumber ||
                      "-"}
                  </b>
                </div>
              </div>
              <button
                type="button"
                className="cancel-btn"
                onClick={closeInvoicePaymentPopup}
                disabled={invoicePaymentSaving}
              >
                ✕
              </button>
            </div>

            <div className="invoice-payment-summary">
              <div className="invoice-payment-summary-card">
                <span
                  style={{ display: "block", fontSize: 11, color: "#64748b" }}
                >
                  Invoice Total
                </span>
                <b>
                  ₹{" "}
                  {Number(invoicePaymentDoc.grandTotal || 0).toLocaleString(
                    "en-IN",
                  )}
                </b>
              </div>
              <div className="invoice-payment-summary-card">
                <span
                  style={{ display: "block", fontSize: 11, color: "#64748b" }}
                >
                  Already Received
                </span>
                <b style={{ color: "#15803d" }}>
                  ₹{" "}
                  {Number(invoicePaymentDoc.paidAmount || 0).toLocaleString(
                    "en-IN",
                  )}
                </b>
              </div>
              <div className="invoice-payment-summary-card">
                <span
                  style={{ display: "block", fontSize: 11, color: "#64748b" }}
                >
                  Balance
                </span>
                <b style={{ color: "#b45309" }}>
                  ₹{" "}
                  {Math.max(
                    0,
                    Number(invoicePaymentDoc.grandTotal || 0) -
                      Number(invoicePaymentDoc.paidAmount || 0),
                  ).toLocaleString("en-IN")}
                </b>
              </div>
            </div>

            <div className="invoice-payment-equation">
              ₹{" "}
              {Number(invoicePaymentDoc.grandTotal || 0).toLocaleString(
                "en-IN",
              )}
              {" - ₹ "}
              {Number(invoicePaymentDoc.paidAmount || 0).toLocaleString(
                "en-IN",
              )}
              {" = ₹ "}
              {Math.max(
                0,
                Number(invoicePaymentDoc.grandTotal || 0) -
                  Number(invoicePaymentDoc.paidAmount || 0),
              ).toLocaleString("en-IN")}{" "}
              Balance
            </div>

            <div className="invoice-payment-form-grid">
              <label>
                Payment Method *
                <select
                  value={invoicePaymentForm.paymentMethod}
                  onChange={(e) =>
                    setInvoicePaymentForm((previous) => ({
                      ...previous,
                      paymentMethod: e.target.value,
                      transactionNumber: "",
                      chequeNumber: "",
                      chequeDate: "",
                      chequeBankName: "",
                    }))
                  }
                >
                  <option value="NEFT">NEFT / Bank Transfer</option>
                  <option value="CHEQUE">Cheque</option>
                </select>
              </label>

              <label>
                Received Amount *
                <input
                  type="number"
                  min="0.01"
                  step="0.01"
                  max={Math.max(
                    0,
                    Number(invoicePaymentDoc.grandTotal || 0) -
                      Number(invoicePaymentDoc.paidAmount || 0),
                  )}
                  value={invoicePaymentForm.amount}
                  onChange={(e) =>
                    setInvoicePaymentForm((previous) => ({
                      ...previous,
                      amount: e.target.value,
                    }))
                  }
                />
              </label>

              <label>
                Payment Date *
                <input
                  type="date"
                  value={invoicePaymentForm.paymentDate}
                  onChange={(e) =>
                    setInvoicePaymentForm((previous) => ({
                      ...previous,
                      paymentDate: e.target.value,
                    }))
                  }
                />
              </label>

              <label>
                Sender / Payer Name *
                <input
                  value={invoicePaymentForm.senderName}
                  onChange={(e) =>
                    setInvoicePaymentForm((previous) => ({
                      ...previous,
                      senderName: e.target.value,
                    }))
                  }
                />
              </label>

              <label>
                Sender Bank Name *
                <input
                  value={invoicePaymentForm.senderBankName}
                  onChange={(e) =>
                    setInvoicePaymentForm((previous) => ({
                      ...previous,
                      senderBankName: e.target.value,
                    }))
                  }
                />
              </label>

              <label>
                Sender Account No.
                <input
                  value={invoicePaymentForm.senderAccountNumber}
                  onChange={(e) =>
                    setInvoicePaymentForm((previous) => ({
                      ...previous,
                      senderAccountNumber: e.target.value,
                    }))
                  }
                />
              </label>

              {invoicePaymentForm.paymentMethod === "NEFT" ? (
                <label className="invoice-payment-full">
                  UTR / Transaction No. *
                  <input
                    value={invoicePaymentForm.transactionNumber}
                    onChange={(e) =>
                      setInvoicePaymentForm((previous) => ({
                        ...previous,
                        transactionNumber: e.target.value,
                      }))
                    }
                  />
                </label>
              ) : (
                <>
                  <label>
                    Cheque No. *
                    <input
                      value={invoicePaymentForm.chequeNumber}
                      onChange={(e) =>
                        setInvoicePaymentForm((previous) => ({
                          ...previous,
                          chequeNumber: e.target.value,
                        }))
                      }
                    />
                  </label>
                  <label>
                    Cheque Date *
                    <input
                      type="date"
                      value={invoicePaymentForm.chequeDate}
                      onChange={(e) =>
                        setInvoicePaymentForm((previous) => ({
                          ...previous,
                          chequeDate: e.target.value,
                        }))
                      }
                    />
                  </label>
                  <label className="invoice-payment-full">
                    Cheque Bank Name *
                    <input
                      value={invoicePaymentForm.chequeBankName}
                      onChange={(e) =>
                        setInvoicePaymentForm((previous) => ({
                          ...previous,
                          chequeBankName: e.target.value,
                        }))
                      }
                    />
                  </label>
                </>
              )}

              <label className="invoice-payment-full invoice-payment-proof">
                Payment Proof *
                <input
                  type="file"
                  accept="image/png,image/jpeg,image/webp,application/pdf"
                  onChange={(e) =>
                    setInvoicePaymentForm((previous) => ({
                      ...previous,
                      proofFile: e.target.files?.[0] || null,
                    }))
                  }
                />
                <small
                  style={{ display: "block", marginTop: 4, color: "#64748b" }}
                >
                  NEFT screenshot / bank receipt or cheque photo.
                </small>
              </label>

              <label className="invoice-payment-full">
                Remarks
                <textarea
                  rows="3"
                  value={invoicePaymentForm.remarks}
                  onChange={(e) =>
                    setInvoicePaymentForm((previous) => ({
                      ...previous,
                      remarks: e.target.value,
                    }))
                  }
                />
              </label>
            </div>

            <div className="finance-popup-footer invoice-payment-footer">
              <button
                className="cancel-btn"
                onClick={closeInvoicePaymentPopup}
                disabled={invoicePaymentSaving}
              >
                Cancel
              </button>
              <button
                className="finance-btn income-btn"
                onClick={saveInvoicePaymentFromFinance}
                disabled={invoicePaymentSaving}
              >
                {invoicePaymentSaving ? "Saving Payment..." : "Save Payment"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function PaperclipIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      aria-hidden="true"
      focusable="false"
      className="finance-paperclip-svg"
    >
      <path
        d="M9.5 12.5 15.7 6.3a3 3 0 0 1 4.2 4.2l-8.1 8.1a5 5 0 1 1-7.1-7.1l8.5-8.5"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function UploadIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      aria-hidden="true"
      focusable="false"
      className="finance-upload-svg"
    >
      <path
        d="M12 16V4m0 0L7.5 8.5M12 4l4.5 4.5M5 15.5v2.25A2.25 2.25 0 0 0 7.25 20h9.5A2.25 2.25 0 0 0 19 17.75V15.5"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function OtherDocumentUpload({ file, existingFileName = "", onFileChange }) {
  const inputId = "finance-other-document-file";

  return (
    <div className="finance-compact-doc">
      <div className="finance-compact-doc-title">Any Other Document</div>

      <input
        id={inputId}
        className="finance-hidden-file-input"
        type="file"
        accept=".pdf,.png,.jpg,.jpeg,.webp,.doc,.docx,.xls,.xlsx"
        onChange={(e) => onFileChange(e.target.files?.[0] || null)}
      />

      <label
        htmlFor={inputId}
        className={`finance-compact-upload-control ${
          file || existingFileName ? "has-file" : ""
        }`}
        title={file?.name || existingFileName || "Attach any other document"}
      >
        <PaperclipIcon />
        <span>{file?.name || existingFileName || "Attach Document"}</span>
      </label>
    </div>
  );
}

function DocumentReferenceField({
  title,
  shortTitle,
  inputId,
  documentValue,
  documents = [],
  existingFiles = [],
  attachedFileName = "",
  loading,
  onDocumentChange,
  file,
  onFileChange,
}) {
  const selectedValue = attachedFileName
    ? `file:${attachedFileName}`
    : documentValue || "";

  return (
    <div className="finance-compact-doc">
      <div className="finance-compact-doc-title">{title}</div>

      <div className="finance-compact-doc-control">
        <div className="finance-compact-select-wrap">
          <span className="finance-compact-leading-icon" aria-hidden="true">
            {attachedFileName ? "📎" : "▱"}
          </span>

          <select
            value={selectedValue}
            onChange={(e) => onDocumentChange(e.target.value)}
            disabled={loading}
            title={`Select existing ${title}`}
          >
            <option value="">
              {loading
                ? "Loading..."
                : `Select Existing ${shortTitle || title}`}
            </option>

            {documents.length > 0 && (
              <optgroup label="ERP Documents">
                {documents.map((doc) => (
                  <option key={doc._id} value={doc._id}>
                    {doc.number ||
                      doc.quotationNumber ||
                      doc.invoiceNumber ||
                      doc._id}
                    {doc.projectName ? ` — ${doc.projectName}` : ""}
                    {doc.client?.companyName
                      ? ` — ${doc.client.companyName}`
                      : ""}
                  </option>
                ))}
              </optgroup>
            )}

            {existingFiles.length > 0 && (
              <optgroup label="Previously Attached">
                {existingFiles.map((name) => (
                  <option key={`file:${name}`} value={`file:${name}`}>
                    📎 {name}
                  </option>
                ))}
              </optgroup>
            )}

            {attachedFileName && !existingFiles.includes(attachedFileName) && (
              <optgroup label="Current Attachment">
                <option value={`file:${attachedFileName}`}>
                  📎 {attachedFileName}
                </option>
              </optgroup>
            )}
          </select>
        </div>

        <input
          id={inputId}
          className="finance-hidden-file-input"
          type="file"
          accept=".pdf,.png,.jpg,.jpeg,.webp,.doc,.docx,.xls,.xlsx"
          onChange={(e) => onFileChange(e.target.files?.[0] || null)}
        />

        <label
          htmlFor={inputId}
          className={`finance-compact-attach-btn ${file ? "has-file" : ""}`}
          title={file ? `Attached: ${file.name}` : `Attach ${title}`}
          aria-label={file ? `Change attached ${title}` : `Attach ${title}`}
        >
          <PaperclipIcon />
        </label>
      </div>
    </div>
  );
}

function SelectDragBox({ title, color, items = [], emptyText, add, getData }) {
  const [selected, setSelected] = useState(null);
  const isRed = color === "red";
  const safeItems = Array.isArray(items) ? items : [];

  const options = useMemo(
    () =>
      safeItems.map((item) => ({
        value: item.value,
        label: item.label,
        raw: item.raw,
      })),
    [safeItems],
  );

  const handleDragStart = (e) => {
    if (!selected) {
      e.preventDefault();
      alert("Please select item first");
      return;
    }

    const dragData = getData(selected.raw);

    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("financeData", JSON.stringify(dragData));
    e.dataTransfer.setData("text/plain", selected.label);
  };

  return (
    <div className={`finance-drag-box ${isRed ? "box-red" : "box-green"}`}>
      <div className="drag-box-header">
        <h4>{title}</h4>

        {add && (
          <button className="add-master-btn" onClick={add}>
            + Add
          </button>
        )}
      </div>

      {safeItems.length === 0 ? (
        <EmptyText text={emptyText} />
      ) : (
        <>
          <Select
            className="finance-react-select"
            classNamePrefix="finance-select"
            value={selected}
            onChange={setSelected}
            options={options}
            placeholder="Select"
            isSearchable
            isClearable
            menuPortalTarget={document.body}
            menuPosition="fixed"
            noOptionsMessage={() => "No result found"}
            styles={{
              menuPortal: (base) => ({ ...base, zIndex: 99999 }),
            }}
          />

          <div
            draggable={!!selected}
            onDragStart={handleDragStart}
            className={`selected-drag-chip ${selected ? "active" : "disabled"}`}
          >
            <span className="drag-icon">⋮⋮</span>
            Drag Selected{selected ? `: ${selected.label}` : ""}
          </div>
        </>
      )}
    </div>
  );
}

function EmptyText({ text }) {
  return <div className="empty-text">{text}</div>;
}

function EntryCard({
  entry,
  type,
  canLock,
  onLock,
  onEdit,
  onRecordPayment,
  onDropToCard,
  onOpenSalesDocument,
  onOpenPurchaseInvoice,
  onOpenPurchaseDocument,
}) {
  const [showDetails, setShowDetails] = useState(false);
  const isIncome = type === "income";

  const baseAmount = Number(entry.amount || 0);
  const gstPercent = Number(entry.gstPercent || 0);
  const tdsPercent = Number(entry.tdsPercent || 0);

  const gstAmount =
    Number(entry.gstAmount || 0) > 0
      ? Number(entry.gstAmount || 0)
      : (baseAmount * gstPercent) / 100;

  const tdsAmount =
    Number(entry.tdsAmount || 0) > 0
      ? Number(entry.tdsAmount || 0)
      : (baseAmount * tdsPercent) / 100;

  const finalTotal =
    Number(entry.totalAmount || 0) > 0
      ? Number(entry.totalAmount || 0)
      : baseAmount + gstAmount - tdsAmount;

  const isInvoiceLinked = Boolean(
    entry.invoiceDocumentId || entry.invoiceNo || entry.isInvoiceGeneratedCard,
  );
  const invoiceAmount = Number(entry.invoiceAmount ?? finalTotal ?? 0);
  const paidAmount = Number(entry.paidAmount || 0);
  const balanceAmount = Math.max(
    0,
    Number(
      entry.balanceAmount !== undefined && entry.balanceAmount !== null
        ? entry.balanceAmount
        : invoiceAmount - paidAmount,
    ),
  );
  const paymentStatus = isInvoiceLinked
    ? entry.paymentStatus ||
      (invoiceAmount > 0 && balanceAmount <= 0 && paidAmount > 0
        ? "paid"
        : paidAmount > 0
          ? "partially_paid"
          : "pending")
    : "";
  const isFullyPaid = paymentStatus === "paid";
  const isPartiallyPaid = paymentStatus === "partially_paid";
  const cardDisplayAmount = isInvoiceLinked ? invoiceAmount : finalTotal;

  const missingOfficeUnit = !entry.officeUnit;
  const missingHead = !entry.head;
  const missingSubHead = !entry.subHead;
  const missingGroup = !entry.groupName;
  const missingProject = !entry.projectName;

  const attachments = [
    entry.quoteFileName && {
      label: "Quotation",
      name: entry.quoteFileName,
      key: entry.quoteFileKey || "",
    },
    entry.proformaFileName && {
      label: "PI",
      name: entry.proformaFileName,
      key: entry.proformaFileKey || "",
    },
    // If an ERP invoice is linked, show only that InvoicePage invoice.
    // Never show a stale/manual invoice attachment as a second invoice.
    entry.invoiceFileName &&
      !entry.invoiceDocumentId &&
      !entry.invoiceNo && {
        label: "Invoice",
        name: entry.invoiceFileName,
        key: entry.invoiceFileKey || "",
      },
    entry.otherDocFileName && {
      label: "Other",
      name: entry.otherDocFileName,
      key: entry.otherDocFileKey || "",
    },
    entry.tdsProofFileName && {
      label: isIncome ? "Received TDS Proof" : "Paid TDS Proof",
      name: entry.tdsProofFileName,
      key: entry.tdsProofFileKey || "",
    },
  ].filter(Boolean);

  const erpDocuments = [
    entry.quoteNo && {
      label: "Quotation",
      name: entry.quoteNo,
      documentId: entry.quoteDocumentId?._id || entry.quoteDocumentId || "",
    },
    entry.proformaNo && {
      label: "PI",
      name: entry.proformaNo,
      documentId:
        entry.proformaDocumentId?._id || entry.proformaDocumentId || "",
    },
    entry.invoiceNo && {
      label: "Invoice",
      name: entry.invoiceNo,
      documentId: entry.invoiceDocumentId?._id || entry.invoiceDocumentId || "",
    },
  ].filter(Boolean);

  const paymentProofs = (Array.isArray(entry.payments) ? entry.payments : [])
    .map((payment, index) => {
      const proofUrl = String(payment?.proofUrl || "").trim();
      if (!proofUrl) return null;

      return {
        label: `Payment Proof ${index + 1}`,
        name:
          payment?.proofFileName ||
          `${payment?.paymentMethod || "Payment"} - ₹ ${Number(payment?.amount || 0).toLocaleString("en-IN")}`,
        proofUrl,
        amount: Number(payment?.amount || 0),
        paymentDate: payment?.paymentDate || "",
      };
    })
    .filter(Boolean);

  const openPaymentProof = async (proof) => {
    try {
      if (!proof?.proofUrl) return;

      const rawUrl = String(proof.proofUrl);
      const url = /^https?:\/\//i.test(rawUrl)
        ? rawUrl
        : `${API_BASE_URL}${rawUrl.startsWith("/") ? "" : "/"}${rawUrl}`;

      const response = await axios.get(url, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
        responseType: "blob",
      });

      const contentType =
        response.headers?.["content-type"] || "application/octet-stream";
      const blob = new Blob([response.data], { type: contentType });
      const fileUrl = URL.createObjectURL(blob);
      const newWindow = window.open(fileUrl, "_blank", "noopener,noreferrer");

      if (!newWindow) alert("Please allow pop-ups to open the payment proof.");
      setTimeout(() => URL.revokeObjectURL(fileUrl), 60000);
    } catch (err) {
      console.error("Payment proof open error:", err);
      alert(
        err.response?.data?.message ||
          err.response?.data?.error ||
          "Payment proof open nahi ho raha.",
      );
    }
  };

  const openAttachment = async (doc) => {
    try {
      if (!doc?.key) return;

      const response = await axios.get(
        `${API_BASE_URL}/api/download?key=${encodeURIComponent(doc.key)}`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
          responseType: "blob",
        },
      );

      const fileURL = URL.createObjectURL(response.data);
      const newWindow = window.open(fileURL, "_blank", "noopener,noreferrer");

      if (!newWindow) {
      }

      setTimeout(() => {
        URL.revokeObjectURL(fileURL);
      }, 60000);
    } catch (err) {
      console.error("Document open error:", err);

      let message = "Document open nahi ho raha.";

      if (err.response?.data instanceof Blob) {
        try {
          const errorText = await err.response.data.text();
          const parsed = JSON.parse(errorText);
          message = parsed?.message || parsed?.error || message;
        } catch (_) {
          // Keep fallback message
        }
      } else {
        message =
          err.response?.data?.message || err.response?.data?.error || message;
      }

      alert(message);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();

    const raw = e.dataTransfer.getData("financeData");
    if (!raw) return;

    const data = JSON.parse(raw);
    onDropToCard(entry, data);
  };

  return (
    <div
      className={`entry-card ${isIncome ? "entry-income" : "entry-expense"}`}
      onDragOver={(e) => {
        if (!entry.isInvoiceGeneratedCard) e.preventDefault();
      }}
      onDrop={(e) => {
        if (!entry.isInvoiceGeneratedCard) handleDrop(e);
      }}
      style={{ position: "relative", overflow: "hidden" }}
    >
      {isFullyPaid && (
        <div
          aria-label="Paid watermark"
          style={{
            position: "absolute",
            left: "50%",
            top: "50%",
            transform: "translate(-50%, -50%) rotate(-18deg)",
            color: "#15803d",
            border: "5px double #15803d",
            borderRadius: 10,
            padding: "5px 22px",
            fontSize: 48,
            lineHeight: 1,
            fontWeight: 950,
            letterSpacing: 7,
            textAlign: "center",
            textTransform: "uppercase",
            opacity: 0.16,
            zIndex: 20,
            pointerEvents: "none",
            whiteSpace: "nowrap",
          }}
        >
          PAID
        </div>
      )}

      <div
        className="entry-card-inner"
        style={{ position: "relative", zIndex: 2 }}
      >
        <div className="entry-card-header">
          <div>
            <div className="entry-type">
              {entry.isInvoiceGeneratedCard
                ? `Invoice ${entry.invoiceNo ? `• ${entry.invoiceNo}` : ""}`
                : isIncome
                  ? "Incoming Entry"
                  : "Outgoing Entry"}
            </div>

            <h2 className="entry-amount">
              ₹ {cardDisplayAmount.toLocaleString("en-IN")}
            </h2>
            {isInvoiceLinked && (
              <small style={{ color: "#64748b", fontWeight: 700 }}>
                Invoice Amount
              </small>
            )}
          </div>

          <div className="entry-header-actions" style={{ paddingRight: 0 }}>
            {isInvoiceLinked && balanceAmount > 0 && (
              <button
                type="button"
                className="entry-edit-btn"
                onClick={() => onRecordPayment(entry)}
                title="Add partial or full payment"
              >
                ✎ Add Payment
              </button>
            )}

            {!entry.locked && !entry.isInvoiceGeneratedCard && (
              <button
                type="button"
                className="entry-edit-btn"
                onClick={() => onEdit(entry)}
              >
                Edit Entry
              </button>
            )}

            {isInvoiceLinked ? (
              <span
                className={
                  entry.locked
                    ? "locked-badge"
                    : isFullyPaid
                      ? "pending-badge"
                      : ""
                }
                style={
                  entry.locked || isFullyPaid
                    ? undefined
                    : {
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                        minHeight: 28,
                        padding: "4px 10px",
                        borderRadius: 999,
                        border: isPartiallyPaid
                          ? "1px solid #fde68a"
                          : "1px solid #fdba74",
                        background: isPartiallyPaid ? "#fef3c7" : "#fff7ed",
                        color: isPartiallyPaid ? "#92400e" : "#c2410c",
                        fontSize: 12,
                        fontWeight: 900,
                        whiteSpace: "nowrap",
                      }
                }
              >
                {entry.locked
                  ? "🔒 Locked"
                  : isFullyPaid
                    ? "Pending Review"
                    : isPartiallyPaid
                      ? "Partially Pending"
                      : "Pending Amount"}
              </span>
            ) : (
              <span className={entry.locked ? "locked-badge" : "pending-badge"}>
                {entry.locked ? "🔒 Locked" : "Pending Review"}
              </span>
            )}
          </div>
        </div>

        {isInvoiceLinked && (
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
              gap: 8,
              margin: "12px 0",
              padding: 10,
              border: "1px solid #e2e8f0",
              borderRadius: 8,
              background: "#f8fafc",
            }}
          >
            <div>
              <span
                style={{
                  display: "block",
                  fontSize: 10,
                  color: "#64748b",
                  fontWeight: 700,
                }}
              >
                Invoice
              </span>
              <b style={{ fontSize: 12 }}>
                ₹ {invoiceAmount.toLocaleString("en-IN")}
              </b>
            </div>
            <div>
              <span
                style={{
                  display: "block",
                  fontSize: 10,
                  color: "#64748b",
                  fontWeight: 700,
                }}
              >
                Received
              </span>
              <b
                style={{
                  fontSize: 12,
                  color: paidAmount > 0 ? "#15803d" : "inherit",
                }}
              >
                ₹ {paidAmount.toLocaleString("en-IN")}
              </b>
            </div>
            <div>
              <span
                style={{
                  display: "block",
                  fontSize: 10,
                  color: "#64748b",
                  fontWeight: 700,
                }}
              >
                Balance
              </span>
              <b
                style={{
                  fontSize: 12,
                  color: balanceAmount > 0 ? "#b45309" : "#15803d",
                }}
              >
                ₹ {balanceAmount.toLocaleString("en-IN")}
              </b>
            </div>
          </div>
        )}

        {isInvoiceLinked && isPartiallyPaid && (
          <div
            style={{
              margin: "-4px 0 12px",
              padding: "7px 10px",
              borderRadius: 7,
              background: "#fffbeb",
              border: "1px solid #fde68a",
              color: "#92400e",
              fontSize: 11,
              fontWeight: 900,
              textAlign: "center",
            }}
          >
            ₹ {invoiceAmount.toLocaleString("en-IN")} - ₹{" "}
            {paidAmount.toLocaleString("en-IN")} = ₹{" "}
            {balanceAmount.toLocaleString("en-IN")} Balance
          </div>
        )}

        {/* Compact card: only Date + Company */}
        <div className="entry-info-grid entry-summary-grid">
          <InfoItem
            label="Date"
            value={
              entry.date
                ? new Date(entry.date).toLocaleDateString("en-IN")
                : "-"
            }
          />

          <InfoItem
            label="Company"
            value={entry.company || "Nil"}
            isMissing={!entry.company}
          />

          <InfoItem
            label="Bank"
            value={
              entry.isInvoiceGeneratedCard && paidAmount <= 0
                ? "Awaiting Payment"
                : entry.paymentMode === "Cash"
                  ? "Cash"
                  : entry.bankAccount || "Nil"
            }
            isMissing={
              !entry.isInvoiceGeneratedCard &&
              entry.paymentMode !== "Cash" &&
              !entry.bankAccount
            }
          />

          <InfoItem
            label="Payment Mode"
            value={
              entry.isInvoiceGeneratedCard && paidAmount <= 0
                ? "Pending"
                : entry.paymentMode ||
                  (paidAmount > 0 ? "As per Invoice Payment" : "Nil")
            }
            isMissing={!entry.isInvoiceGeneratedCard && !entry.paymentMode}
          />
          <div className="entry-amount-summary-item">
            <div className="amount-breakup entry-expanded-amount-breakup">
              <div>
                <b>Amount:</b> ₹ {baseAmount.toLocaleString("en-IN")}
                {gstAmount > 0 &&
                  ` + ₹ ${gstAmount.toLocaleString("en-IN")} GST (${gstPercent}%)`}
                {tdsAmount > 0 &&
                  ` - ₹ ${tdsAmount.toLocaleString("en-IN")} TDS (${tdsPercent}%)`}
                {` = ₹ ${finalTotal.toLocaleString("en-IN")}`}
              </div>

              {getTdsStatus(entry) && (
                <div
                  className={`tds-mini-status ${getTdsStatus(entry).className}`}
                >
                  <span>{getTdsStatus(entry).label}</span>

                  {getTdsStatus(entry).amount > 0 && (
                    <strong>
                      ₹ {getTdsStatus(entry).amount.toLocaleString("en-IN")}
                    </strong>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        <button
          type="button"
          className={`entry-view-more-btn ${showDetails ? "open" : ""}`}
          onClick={() => setShowDetails((prev) => !prev)}
          aria-expanded={showDetails}
        >
          <span>{showDetails ? "View Less" : "View More"}</span>
          <span className="entry-view-more-arrow">
            {showDetails ? "▲" : "▼"}
          </span>
        </button>
        {showDetails && (
          <div className="entry-expanded-details">
            {/* All mapping / accounting details */}
            <div className="entry-info-grid entry-detail-grid">
              <InfoItem
                label="Office Unit"
                value={entry.officeUnit || "Nil"}
                isMissing={missingOfficeUnit}
              />

              <InfoItem
                label={isIncome ? "Income Parent Head" : "Expense Parent Head"}
                value={entry.head || "Nil"}
                isMissing={missingHead}
              />

              <InfoItem
                label={isIncome ? "Income Sub Head" : "Expense Sub Head"}
                value={entry.subHead || "Nil"}
                isMissing={missingSubHead}
              />

              <InfoItem
                label="Group"
                value={entry.groupName || "Nil"}
                isMissing={missingGroup}
              />

              <InfoItem
                label="Project"
                value={entry.projectName || "Nil"}
                isMissing={missingProject}
              />

              <InfoItem
                label={isIncome ? "Customer" : "Vendor"}
                value={entry.partyName || "Nil"}
                isMissing={!entry.partyName}
              />

              <InfoItem
                label="Asset"
                value={
                  entry.classificationType === "asset"
                    ? entry.classification || "Nil"
                    : "Nil"
                }
                isMissing={
                  entry.classificationType !== "asset" || !entry.classification
                }
              />

              <InfoItem
                label="Liability"
                value={
                  entry.classificationType === "liability"
                    ? entry.classification || "Nil"
                    : "Nil"
                }
                isMissing={
                  entry.classificationType !== "liability" ||
                  !entry.classification
                }
              />

              {entry.billNo && (
                <InfoItem label="Bill No." value={entry.billNo} />
              )}

              {tdsPercent > 0 && (
                <InfoItem
                  label="TDS"
                  value={`${tdsPercent}% | ₹ ${tdsAmount.toLocaleString("en-IN")}`}
                />
              )}
              {(Number(entry.tdsSettlementAmount || 0) > 0 ||
                entry.tdsProofFileName) && (
                <InfoItem
                  label={isIncome ? "Received TDS" : "Paid TDS"}
                  value={`₹ ${Number(entry.tdsSettlementAmount || 0).toLocaleString("en-IN")}`}
                />
              )}
            </div>

            {(attachments.length > 0 ||
              erpDocuments.length > 0 ||
              paymentProofs.length > 0) && (
              <div className="entry-documents-section">
                <div className="entry-documents-title">Documents</div>

                <div className="entry-documents-list">
                  {erpDocuments.map((doc) =>
                    doc.documentId ? (
                      <button
                        type="button"
                        key={`erp-${doc.label}-${doc.name}`}
                        className="entry-document-chip erp-document-chip entry-document-link"
                        title={`Open ${doc.label}: ${doc.name}`}
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          if (
                            entry.type === "expense" &&
                            (entry.invoiceSource === "purchase" ||
                              entry.autoGenerated)
                          ) {
                            onOpenPurchaseDocument?.(entry, doc);
                            return;
                          }

                          if (
                            doc.label === "Invoice" &&
                            entry.invoiceSource === "purchase"
                          ) {
                            onOpenPurchaseInvoice?.(entry);
                            return;
                          }

                          onOpenSalesDocument?.(doc.documentId);
                        }}
                      >
                        <span className="entry-document-icon">▱</span>
                        <span>
                          <small>{doc.label}</small>
                          <b>{doc.name}</b>
                        </span>
                        <span className="entry-document-open-icon">↗</span>
                      </button>
                    ) : (
                      <div
                        key={`erp-${doc.label}-${doc.name}`}
                        className="entry-document-chip erp-document-chip entry-document-unavailable"
                        title={`${doc.label}: ${doc.name} — document ID not available`}
                      >
                        <span className="entry-document-icon">▱</span>
                        <span>
                          <small>{doc.label}</small>
                          <b>{doc.name}</b>
                        </span>
                      </div>
                    ),
                  )}

                  {paymentProofs.map((proof) => (
                    <button
                      type="button"
                      key={`payment-proof-${proof.label}-${proof.name}`}
                      className="entry-document-chip payment-proof-chip entry-document-link"
                      title={`Open ${proof.label}: ${proof.name}`}
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        openPaymentProof(proof);
                      }}
                    >
                      <span className="entry-document-icon">🧾</span>
                      <span>
                        <small>{proof.label}</small>
                        <b>{proof.name}</b>
                        {proof.paymentDate && (
                          <em className="entry-proof-date">
                            {new Date(proof.paymentDate).toLocaleDateString(
                              "en-IN",
                            )}
                          </em>
                        )}
                      </span>
                      <span className="entry-document-open-icon">↗</span>
                    </button>
                  ))}

                  {attachments.map((doc) =>
                    doc.key ? (
                      <button
                        type="button"
                        key={`file-${doc.label}-${doc.name}`}
                        className="entry-document-chip entry-document-link"
                        title={`Open ${doc.label}: ${doc.name}`}
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          openAttachment(doc);
                        }}
                      >
                        <span className="entry-document-icon">📎</span>
                        <span>
                          <small>{doc.label}</small>
                          <b>{doc.name}</b>
                        </span>
                        <span className="entry-document-open-icon">↗</span>
                      </button>
                    ) : (
                      <div
                        key={`file-${doc.label}-${doc.name}`}
                        className="entry-document-chip entry-document-unavailable"
                        title={`${doc.label}: ${doc.name} — file path not available`}
                      >
                        <span className="entry-document-icon">📎</span>
                        <span>
                          <small>{doc.label}</small>
                          <b>{doc.name}</b>
                        </span>
                      </div>
                    ),
                  )}
                </div>
              </div>
            )}

            {!entry.locked &&
              canLock &&
              !entry.isInvoiceGeneratedCard &&
              (!isInvoiceLinked || isFullyPaid) && (
                <button className="lock-btn" onClick={() => onLock(entry._id)}>
                  🔒 Lock Entry
                </button>
              )}
          </div>
        )}
      </div>
    </div>
  );
}

function InfoItem({ label, value, isMissing }) {
  return (
    <div className={`entry-info-item ${isMissing ? "missing-info-item" : ""}`}>
      <span>{label}</span>
      <b>{isMissing ? `!! ${value}` : value}</b>
    </div>
  );
}
